import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  User,
  Briefcase,
  FileText,
  Calendar,
  RefreshCw,
  Activity,
  DollarSign,
  Folder,
  Lock,
  Unlock,
  Save,
  Mail,
  Phone,
  MapPin,
  CreditCard,
  HelpCircle,
  Edit,
  Archive,
  MoreVertical,
  ChevronRight,
  ChevronDown,
  Plus,
  Trash2,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Download,
  Check,
  Search,
  Shield,
  Building2
} from 'lucide-react';
import http from '../api/http';

/* ════════════════════════════════════════════════════════════════════
   Éléments de présentation uniquement (aucune logique métier ici)
   ════════════════════════════════════════════════════════════════════ */

const cx = (...c) => c.filter(Boolean).join(' ');

const fieldId = (label) =>
  'f-' + String(label).normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

const controlCls =
  'w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 transition placeholder:text-slate-400 hover:border-slate-400 focus:border-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-600/20 disabled:cursor-not-allowed disabled:bg-slate-50 disabled:text-slate-500';

const btnPrimary =
  'inline-flex items-center justify-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-blue-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-600/40 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60';

const btnSecondary =
  'inline-flex items-center justify-center gap-2 rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-600/40 disabled:cursor-not-allowed disabled:opacity-50';

const iconBtn =
  'rounded-lg p-2 text-slate-400 transition hover:bg-slate-100 hover:text-slate-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-600/40';

const BADGE_TONES = {
  green: 'bg-emerald-50 text-emerald-700 ring-emerald-600/20',
  slate: 'bg-slate-100 text-slate-600 ring-slate-500/20',
  blue: 'bg-blue-50 text-blue-700 ring-blue-600/20',
  red: 'bg-red-50 text-red-700 ring-red-600/20',
};

const Badge = ({ tone = 'slate', children }) => (
  <span className={cx('inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset', BADGE_TONES[tone])}>
    {children}
  </span>
);

// Composant d'aide avec tooltip (visible au survol et au clavier)
const HelpTooltip = ({ text }) => (
  <span className="group relative inline-flex">
    <button
      type="button"
      aria-label={text}
      className="rounded-full text-slate-400 transition hover:text-slate-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-600/40"
    >
      <HelpCircle className="h-3.5 w-3.5" />
    </button>
    <span
      role="tooltip"
      className="pointer-events-none absolute bottom-full left-1/2 z-10 mb-2 w-max max-w-[16rem] -translate-x-1/2 rounded-md bg-slate-900 px-2.5 py-1.5 text-xs leading-snug text-white opacity-0 shadow-lg transition-opacity group-focus-within:opacity-100 group-hover:opacity-100"
    >
      {text}
    </span>
  </span>
);

const Field = ({ label, icon: Icon, help, required, htmlFor, className = '', children, after }) => (
  <div className={className}>
    <div className="mb-1.5 flex items-center gap-1.5">
      <label htmlFor={htmlFor} className="flex items-center gap-1.5 text-sm font-medium text-slate-700">
        {Icon && <Icon className="h-3.5 w-3.5 text-slate-400" />}
        {label}
        {required && <span className="text-red-600" aria-hidden="true">*</span>}
      </label>
      {help && <HelpTooltip text={help} />}
    </div>
    {children}
    {after}
  </div>
);

const TextField = ({ label, icon, help, className, after, ...props }) => {
  const id = fieldId(label);
  return (
    <Field label={label} icon={icon} help={help} required={props.required} htmlFor={id} className={className} after={after}>
      <input id={id} type="text" className={controlCls} {...props} />
    </Field>
  );
};

const SelectField = ({ label, icon, help, className, after, children, ...props }) => {
  const id = fieldId(label);
  return (
    <Field label={label} icon={icon} help={help} required={props.required} htmlFor={id} className={className} after={after}>
      <div className="relative">
        <select id={id} className={cx(controlCls, 'appearance-none pr-9')} {...props}>{children}</select>
        <ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
      </div>
    </Field>
  );
};

// Choix à deux options : appelle simplement onSelect(valeur)
const Segmented = ({ label, help, value, options, onSelect, className }) => (
  <Field label={label} help={help} className={className}>
    <div role="radiogroup" aria-label={label} className="flex rounded-lg bg-slate-100 p-1">
      {options.map((o) => {
        const active = String(value) === String(o.value);
        return (
          <button
            key={String(o.value)}
            type="button"
            role="radio"
            aria-checked={active}
            onClick={() => onSelect(o.value)}
            className={cx(
              'flex-1 rounded-md px-3 py-1.5 text-sm font-medium transition focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-600/40',
              active ? 'bg-white text-blue-700 shadow-sm' : 'text-slate-500 hover:text-slate-800',
            )}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  </Field>
);

const CheckRow = ({ id, checked, onChange, label }) => (
  <div className="flex items-center gap-2.5">
    <input
      type="checkbox"
      id={id}
      checked={checked}
      onChange={onChange}
      className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-600"
    />
    <label htmlFor={id} className="text-sm text-slate-700">{label}</label>
  </div>
);

// Conteneur d'un onglet
const Panel = ({ title, description, action, children }) => (
  <div className="rounded-xl border border-slate-200 bg-white">
    <header className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 px-6 py-4">
      <div>
        <h2 className="text-lg font-semibold text-slate-900">{title}</h2>
        {description && <p className="text-sm text-slate-500">{description}</p>}
      </div>
      {action}
    </header>
    <div className="px-6">{children}</div>
  </div>
);

// Titre et explication à gauche, champs à droite
const Section = ({ title, description, plain = false, action, children }) => (
  <section className="grid gap-x-10 gap-y-4 border-t border-slate-200 py-6 first:border-t-0 lg:grid-cols-[220px_minmax(0,1fr)]">
    <div className="flex items-start justify-between">
      <div className="flex-1">
        <h3 className="text-sm font-semibold text-slate-900">{title}</h3>
        {description && <p className="mt-1 text-sm leading-snug text-slate-500">{description}</p>}
      </div>
      {action && <div className="ml-4">{action}</div>}
    </div>
    {plain ? (
      <div className="min-w-0">{children}</div>
    ) : (
      <div className="grid grid-cols-[repeat(auto-fill,minmax(16rem,1fr))] gap-x-5 gap-y-4">{children}</div>
    )}
  </section>
);

const StatList = ({ children }) => (
  <dl className="grid grid-cols-[repeat(auto-fill,minmax(16rem,1fr))] gap-x-5 gap-y-4">{children}</dl>
);

const Stat = ({ label, children }) => (
  <div className="min-w-0">
    <dt className="text-sm text-slate-500">{label}</dt>
    <dd className="mt-0.5 break-words text-base font-semibold text-slate-900">{children}</dd>
  </div>
);

const TableWrap = ({ children }) => (
  <div className="overflow-x-auto rounded-lg border border-slate-200">
    <table className="min-w-full divide-y divide-slate-200">{children}</table>
  </div>
);

const Th = ({ children }) => (
  <th scope="col" className="whitespace-nowrap bg-slate-50 px-4 py-2.5 text-left text-xs font-semibold text-slate-600">{children}</th>
);

const Td = ({ children, className }) => (
  <td className={cx('whitespace-nowrap px-4 py-3 text-sm text-slate-700', className)}>{children}</td>
);

const EmptyState = ({ children }) => (
  <p className="py-6 text-center text-sm text-slate-500">{children}</p>
);

const LoadingBlock = ({ className = 'py-8' }) => (
  <div className={cx('text-center text-sm text-slate-500', className)}>Chargement...</div>
);

const Modal = ({ title, children }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 p-4 backdrop-blur-sm">
    <div role="dialog" aria-modal="true" aria-label={title} className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl ring-1 ring-black/10">
      <h3 className="mb-5 text-lg font-semibold text-slate-900">{title}</h3>
      {children}
    </div>
  </div>
);

const ModalActions = ({ children }) => (
  <div className="mt-6 flex justify-end gap-2 border-t border-slate-100 pt-5">{children}</div>
);

// Champs communs aux modales d'ajout et de modification d'un contrat
const ContratFields = ({ formData, setFormData, typesContrat, suffix = '', showEtat = false }) => (
  <div className="space-y-4">
    <SelectField
      label="Type de contrat"
      required
      value={formData.type_contrat_id}
      onChange={(e) => setFormData({ ...formData, type_contrat_id: parseInt(e.target.value) })}
    >
      <option value="">Sélectionner</option>
      {typesContrat.map(tc => (
        <option key={tc.idtype_contrat} value={tc.idtype_contrat}>{tc.nom}</option>
      ))}
    </SelectField>
    <TextField
      label="Date d'entrée"
      type="date"
      required
      value={formData.date_entree_contrat}
      onChange={(e) => setFormData({ ...formData, date_entree_contrat: e.target.value })}
    />
    <TextField
      label="Date de fin"
      type="date"
      value={formData.date_fin_contrat}
      onChange={(e) => setFormData({ ...formData, date_fin_contrat: e.target.value })}
    />
    <div className="space-y-2.5 pt-1">
      <CheckRow
        id={`essai${suffix}`}
        checked={formData.essai === 1}
        onChange={(e) => setFormData({ ...formData, essai: e.target.checked ? 1 : 0 })}
        label="Période d'essai"
      />
      <CheckRow
        id={`verification${suffix}`}
        checked={formData.verification === 1}
        onChange={(e) => setFormData({ ...formData, verification: e.target.checked ? 1 : 0 })}
        label="Vérifié"
      />
      <CheckRow
        id={`validation${suffix}`}
        checked={formData.validation === 1}
        onChange={(e) => setFormData({ ...formData, validation: e.target.checked ? 1 : 0 })}
        label="Validé"
      />
      {showEtat && (
        <CheckRow
          id={`etat${suffix}`}
          checked={formData.etat === 1}
          onChange={(e) => setFormData({ ...formData, etat: e.target.checked ? 1 : 0 })}
          label="Actif"
        />
      )}
    </div>
  </div>
);

/* ════════════════════════════════════════════════════════════════════
   Page
   ════════════════════════════════════════════════════════════════════ */

const EmployeDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('infos');
  const [employe, setEmploye] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Données de référence
  // eslint-disable-next-line no-unused-vars
  const [typesContrat, setTypesContrat] = useState([]);
  // eslint-disable-next-line no-unused-vars
  const [loadingRefs, setLoadingRefs] = useState(false);
  const [refsLoaded, setRefsLoaded] = useState(false);

  const tabs = [
    { id: 'infos', label: 'Informations personnelles', icon: User },
    { id: 'poste', label: 'Poste & Organisation', icon: Briefcase },
    { id: 'contrats', label: 'Contrats', icon: FileText },
    { id: 'conges', label: 'Congés', icon: Calendar },
    { id: 'mobilite', label: 'Mobilité & Carrière', icon: RefreshCw },
    { id: 'absences', label: 'Absences', icon: Activity },
    { id: 'paie', label: 'Paie', icon: DollarSign },
    { id: 'documents', label: 'Documents', icon: Folder },
    { id: 'compte', label: 'Compte utilisateur', icon: Lock },
  ];

  useEffect(() => {
    loadEmploye();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (employe && !refsLoaded) {
      loadReferenceData();
      setRefsLoaded(true);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employe, refsLoaded]);

  const loadReferenceData = async () => {
    setLoadingRefs(true);
    try {
      const typesContratRes = await http.get('/api/types-contrat').catch(() => ({ data: [] }));
      setTypesContrat(typesContratRes.data || []);
    } catch (error) {
      console.error('Erreur chargement références:', error);
    } finally {
      setLoadingRefs(false);
    }
  };

  const loadEmploye = async () => {
    try {
      const response = await http.get(`/api/personnel/${id}`);
      setEmploye(response.data);
    } catch (error) {
      console.error('Erreur chargement employé:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await http.put(`/api/personnel/${id}`, employe);
      alert('Informations enregistrées avec succès');
    } catch (error) {
      console.error('Erreur sauvegarde:', error);
      alert('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center bg-slate-50">
        <div role="status" aria-label="Chargement" className="h-10 w-10 animate-spin rounded-full border-2 border-slate-200 border-t-blue-600 motion-reduce:animate-none"></div>
      </div>
    );
  }

  if (!employe) {
    return (
      <div className="flex h-screen items-center justify-center bg-slate-50">
        <p className="text-slate-500">Employé non trouvé</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* En-tête fixe */}
      <div className="sticky top-16 z-40 border-b border-slate-200 bg-white">
        <div className="flex w-full items-center justify-between gap-4 px-4 py-3 sm:px-6 lg:px-8">
          <div className="flex min-w-0 items-center gap-4">
            <button
              onClick={() => navigate('/employes')}
              aria-label="Retour à la liste des employés"
              title="Retour à la liste"
              className={iconBtn}
            >
              <ChevronRight className="h-5 w-5 rotate-180" />
            </button>
            {employe.photo_profil ? (
              <img
                src={employe.photo_profil}
                alt={`${employe.prenom} ${employe.nom}`}
                className="h-14 w-14 shrink-0 rounded-full object-cover"
              />
            ) : (
              <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-blue-100 text-lg font-semibold text-blue-700">
                {employe.nom?.[0]}{employe.prenom?.[0]}
              </div>
            )}
            <div className="min-w-0">
              <h1 className="truncate text-xl font-semibold text-slate-900">
                {employe.nom} {employe.prenom}
              </h1>
              <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-slate-500">
                <span className="flex items-center gap-1.5 font-mono">
                  <CreditCard className="h-4 w-4" />
                  {employe.matricule}
                </span>
                <Badge tone={employe.active === 1 ? 'green' : 'red'}>
                  {employe.active === 1 ? 'Actif' : 'Inactif'}
                </Badge>
              </div>
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-1">
            <button
              onClick={handleSave}
              disabled={saving}
              className={cx(btnPrimary, 'mr-2')}
            >
              <Save className="h-4 w-4" />
              {saving ? 'Enregistrement...' : 'Enregistrer'}
            </button>
            <button className={iconBtn} aria-label="Envoyer un e-mail" title="E-mail">
              <Mail className="h-5 w-5" />
            </button>
            <button className={iconBtn} aria-label="Archiver" title="Archiver">
              <Archive className="h-5 w-5" />
            </button>
            <button className={iconBtn} aria-label="Plus d'actions" title="Plus d'actions">
              <MoreVertical className="h-5 w-5" />
            </button>
          </div>
        </div>
      </div>

      <div className="w-full px-4 py-6 sm:px-6 lg:px-8 xl:grid xl:grid-cols-[240px_minmax(0,1fr)] xl:gap-8">
        {/* Navigation par onglets : barre horizontale, puis menu latéral sur grand écran */}
        <nav
          aria-label="Sections de la fiche"
          className="-mx-4 mb-6 flex gap-1 overflow-x-auto border-b border-slate-200 px-4 sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8 xl:sticky xl:top-24 xl:mx-0 xl:mb-0 xl:flex-col xl:self-start xl:overflow-visible xl:border-b-0 xl:px-0"
        >
          {tabs.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                aria-current={isActive ? 'page' : undefined}
                className={cx(
                  'flex items-center gap-2.5 whitespace-nowrap border-b-2 px-3 py-3 text-sm font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-600/40 xl:rounded-lg xl:border-b-0 xl:py-2',
                  isActive
                    ? 'border-blue-600 text-blue-700 xl:bg-blue-50'
                    : 'border-transparent text-slate-600 hover:text-slate-900 xl:hover:bg-slate-100',
                )}
              >
                <Icon className={cx('h-4 w-4 shrink-0', isActive ? 'text-blue-600' : 'text-slate-400')} />
                {tab.label}
              </button>
            );
          })}
        </nav>

        {/* Contenu des onglets */}
        <main className="min-w-0">
          {activeTab === 'infos' && <InformationsPersonnelles employe={employe} setEmploye={setEmploye} />}
          {activeTab === 'poste' && <PosteOrganisation
            employe={employe}
            setEmploye={setEmploye}
          />}
          {activeTab === 'contrats' && <Contrats employe={employe} />}
          {activeTab === 'conges' && <Conges employe={employe} />}
          {activeTab === 'mobilite' && <MobiliteCarriere employe={employe} />}
          {activeTab === 'absences' && <Absences employe={employe} />}
          {activeTab === 'paie' && <Paie employe={employe} />}
          {activeTab === 'documents' && <Documents employe={employe} />}
          {activeTab === 'compte' && <CompteUtilisateur employe={employe} />}
        </main>
      </div>
    </div>
  );
};

// Calcul des parts IGR selon la grille ivoirienne
const calculerPartsIGR = (situation, enfants) => {
  const enfant = parseInt(enfants) || 0;
  const partsBase = 1; // Part de base pour célibataire/divorcé/veuf sans enfant

  if (!situation) return partsBase;

  const situationLower = situation.toLowerCase();

  // Célibataire, divorcé ou veuf sans enfant
  if ((situationLower.includes('célibataire') || situationLower.includes('divorcé') || situationLower.includes('veuf')) && enfant === 0) {
    return partsBase;
  }

  // Marié sans enfant
  if (situationLower.includes('marié') && enfant === 0) {
    return 2;
  }

  // Célibataire ou divorcé avec 1 enfant
  if ((situationLower.includes('célibataire') || situationLower.includes('divorcé')) && enfant === 1) {
    return 2;
  }

  // Marié ou veuf avec 1 enfant
  if ((situationLower.includes('marié') || situationLower.includes('veuf')) && enfant === 1) {
    return 2.5;
  }

  // Célibataire ou divorcé avec 2 enfants
  if ((situationLower.includes('célibataire') || situationLower.includes('divorcé')) && enfant === 2) {
    return 2.5;
  }

  // Marié ou veuf avec 2 enfants
  if ((situationLower.includes('marié') || situationLower.includes('veuf')) && enfant === 2) {
    return 3;
  }

  // Célibataire ou divorcé avec 3 enfants
  if ((situationLower.includes('célibataire') || situationLower.includes('divorcé')) && enfant === 3) {
    return 3;
  }

  // Marié ou veuf avec 3 enfants
  if ((situationLower.includes('marié') || situationLower.includes('veuf')) && enfant === 3) {
    return 3.5;
  }

  // Célibataire ou divorcé avec 4 enfants
  if ((situationLower.includes('célibataire') || situationLower.includes('divorcé')) && enfant === 4) {
    return 3.5;
  }

  // Marié ou veuf avec 4 enfants
  if ((situationLower.includes('marié') || situationLower.includes('veuf')) && enfant === 4) {
    return 4;
  }

  // Pour 5 enfants et plus : +0.5 par enfant supplémentaire, plafond 5
  let parts = 0;
  if (situationLower.includes('marié') || situationLower.includes('veuf')) {
    parts = 3.5 + (enfant - 3) * 0.5; // À partir de 3 enfants
  } else {
    parts = 2.5 + (enfant - 2) * 0.5; // À partir de 2 enfants
  }

  return Math.min(parts, 5); // Plafond à 5 parts
};

// Onglet Informations personnelles
const InformationsPersonnelles = ({ employe, setEmploye }) => (
  <Panel title="Informations personnelles">
    <Section title="État civil" description="Identité de l'employé.">
      <TextField
        label="Nom"
        help="Nom de famille de l'employé"
        value={employe.nom || ''}
        onChange={(e) => setEmploye({ ...employe, nom: e.target.value })}
      />
      <TextField
        label="Prénom"
        help="Prénom de l'employé"
        value={employe.prenom || ''}
        onChange={(e) => setEmploye({ ...employe, prenom: e.target.value })}
      />
      <TextField
        label="Autres prénoms"
        help="Deuxième prénom ou prénoms additionnels"
        value={employe.autres_prenom || ''}
        onChange={(e) => setEmploye({ ...employe, autres_prenom: e.target.value })}
      />
      <Segmented
        label="Sexe"
        help="Genre de l'employé"
        value={employe.sexe || ''}
        onSelect={(v) => setEmploye({ ...employe, sexe: v })}
        options={[{ value: 'M', label: 'Masculin' }, { value: 'F', label: 'Féminin' }]}
      />
      <TextField
        label="Date de naissance"
        type="date"
        help="Date de naissance de l'employé"
        value={employe.date_naissance || ''}
        onChange={(e) => setEmploye({ ...employe, date_naissance: e.target.value })}
      />
      <TextField
        label="Lieu de naissance"
        type="text"
        help="Lieu de naissance de l'employé"
        value={employe.lieu_naissance || ''}
        onChange={(e) => setEmploye({ ...employe, lieu_naissance: e.target.value })}
      />
      <TextField
        label="Nationalité"
        help="Nationalité de l'employé"
        value={employe.nationalite || ''}
        onChange={(e) => setEmploye({ ...employe, nationalite: e.target.value })}
      />
    </Section>

    <Section title="Situation familiale">
      <SelectField
        label="Situation matrimoniale"
        help="État civil de l'employé"
        value={employe.situation_matrimoniale || ''}
        onChange={(e) => {
          const newSituation = e.target.value;
          const parts = calculerPartsIGR(newSituation, employe.nombre_enfant_charge);
          setEmploye({ ...employe, situation_matrimoniale: newSituation, part_igr: parts });
        }}
      >
        <option value="">Sélectionner</option>
        <option value="Célibataire">Célibataire</option>
        <option value="Marié(e)">Marié(e)</option>
        <option value="Divorcé(e)">Divorcé(e)</option>
        <option value="Veuf/Veuve">Veuf/Veuve</option>
      </SelectField>
      <TextField
        label="Nombre d'enfants à charge"
        type="number"
        help="Nombre d'enfants pris en charge"
        value={employe.nombre_enfant_charge || 0}
        onChange={(e) => {
          const newEnfants = parseInt(e.target.value) || 0;
          const parts = calculerPartsIGR(employe.situation_matrimoniale, newEnfants);
          setEmploye({ ...employe, nombre_enfant_charge: newEnfants, part_igr: parts });
        }}
      />
    </Section>

    <Section title="Formation">
      <TextField
        label="Domaine d'étude"
        help="Domaine d'études de l'employé"
        value={employe.domaine_etude || ''}
        onChange={(e) => setEmploye({ ...employe, domaine_etude: e.target.value })}
      />
      <TextField
        label="Niveau d'étude"
        help="Niveau d'études atteint"
        value={employe.niveau_etude || ''}
        onChange={(e) => setEmploye({ ...employe, niveau_etude: e.target.value })}
      />
    </Section>

    <Section title="Contact" description="Pour joindre l'employé, y compris en cas d'urgence.">
      <TextField
        label="Téléphone"
        icon={Phone}
        help="Numéro de téléphone de l'employé"
        value={employe.numtel || ''}
        onChange={(e) => setEmploye({ ...employe, numtel: e.target.value })}
      />
      <TextField
        label="Email"
        icon={Mail}
        type="email"
        help="Adresse email professionnelle"
        value={employe.adressemail || ''}
        onChange={(e) => setEmploye({ ...employe, adressemail: e.target.value })}
      />
      <TextField
        label="Adresse"
        icon={MapPin}
        help="Adresse complète de l'employé"
        value={employe.adresse_complete || ''}
        onChange={(e) => setEmploye({ ...employe, adresse_complete: e.target.value })}
      />
      <TextField
        label="Personne à contacter"
        help="Personne à contacter en cas d'urgence"
        value={employe.personne_contact || ''}
        onChange={(e) => setEmploye({ ...employe, personne_contact: e.target.value })}
      />
    </Section>

    <Section title="Pièce d'identité" description="Type, numéro et validité de la pièce.">
      <div className="grid gap-3">
        <label className="block text-sm font-medium text-slate-700">
          Type de pièce d'identité
        </label>
        <select
          className={controlCls}
          value={employe.type_piece_id || ''}
          onChange={(e) => setEmploye({ ...employe, type_piece_id: e.target.value })}
        >
          <option value="">Sélectionner</option>
          <option value="Passeport">Passeport</option>
          <option value="Permis de conduire">Permis de conduire</option>
          <option value="Carte d'identité nationale">Carte d'identité nationale</option>
          <option value="Autre">Autre</option>
        </select>
      </div>
      <TextField
        label="Numéro de pièce"
        help="Numéro de la pièce d'identité"
        value={employe.num_piece_id || ''}
        onChange={(e) => setEmploye({ ...employe, num_piece_id: e.target.value })}
      />
      <TextField
        label="Lieu de délivrance"
        help="Lieu de délivrance de la pièce"
        value={employe.lieu_delivrance_piece || ''}
        onChange={(e) => setEmploye({ ...employe, lieu_delivrance_piece: e.target.value })}
      />
      <TextField
        label="Date d'expiration"
        type="date"
        help="Date d'expiration de la pièce"
        value={employe.date_expiration_piece || ''}
        onChange={(e) => setEmploye({ ...employe, date_expiration_piece: e.target.value })}
      />
    </Section>

    <Section title="Protection sociale et fiscalité">
      <TextField
        label="Numéro CNSS"
        help="Numéro de sécurité sociale (obligatoire pour la caisse sociale)"
        value={employe.numero_cnss || ''}
        onChange={(e) => setEmploye({ ...employe, numero_cnss: e.target.value })}
      />
      <TextField
        label="IFU"
        help="Identifiant Fiscal Unique"
        value={employe.numero_ifu || ''}
        onChange={(e) => setEmploye({ ...employe, numero_ifu: e.target.value })}
      />
    </Section>
  </Panel>
);

// Onglet Poste & Organisation
const PosteOrganisation = ({ employe, setEmploye }) => {
  const [departements, setDepartments] = useState([]);
  const [businessUnits, setBusinessUnits] = useState([]);
  const [grades, setGrades] = useState([]);
  const [titres, setTitres] = useState([]);
  const [typesPersonnel, setTypesPersonnel] = useState([]);
  const [campagnesList, setCampagnesList] = useState([]);
  const [marques, setMarques] = useState([]);
  const [managers, setManagers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadReferenceData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employe?.idpersonnel, employe?.filiale_id]);

  const loadReferenceData = async () => {
    if (!employe?.idpersonnel) return;
    setLoading(true);
    try {
      const params = {};
      if (employe.filiale_id) {
        params.filiale_id = employe.filiale_id;
      }

      const [deptRes, buRes, gradesRes, titresRes, typesRes, campagnesRes, marquesRes, managersRes] = await Promise.all([
        http.get('/api/departements', { params }).catch(e => { console.error('[POSTE] Erreur départements:', e); return { data: [] }; }),
        http.get('/api/business-units').catch(e => { console.error('[POSTE] Erreur business units:', e); return { data: [] }; }),
        http.get('/api/grades').catch(e => { console.error('[POSTE] Erreur grades:', e); return { data: [] }; }),
        http.get('/api/titres', { params }).catch(e => { console.error('[POSTE] Erreur titres:', e); return { data: [] }; }),
        http.get('/api/types-personnel', { params }).catch(e => { console.error('[POSTE] Erreur types personnel:', e); return { data: [] }; }),
        http.get('/api/campagnes', { params }).catch(e => { console.error('[POSTE] Erreur campagnes:', e); return { data: [] }; }),
        http.get('/api/marques').catch(e => { console.error('[POSTE] Erreur marques:', e); return { data: [] }; }),
        http.get('/api/personnel', { params }).catch(e => { console.error('[POSTE] Erreur managers:', e); return { data: [] }; })
      ]);

      setDepartments(deptRes.data || []);
      setBusinessUnits(buRes.data || []);
      setGrades(gradesRes.data || []);
      setTitres(titresRes.data || []);
      setTypesPersonnel(typesRes.data || []);
      setCampagnesList(campagnesRes.data || []);
      setMarques(marquesRes.data || []);
      setManagers(managersRes.data?.data || []);
    } catch (error) {
      console.error('[POSTE] Erreur chargement références:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <LoadingBlock className="p-6" />;
  }

  return (
    <Panel title="Poste & Organisation">
      <Section title="Affectation" description="Rattachement et activité de l'employé.">
        <SelectField
          label="Département"
          help="Département d'affectation de l'employé"
          value={employe.departement_id || ''}
          onChange={(e) => setEmploye({ ...employe, departement_id: parseInt(e.target.value) || null })}
        >
          <option value="">Sélectionner</option>
          {departements && departements.map(d => (
            <option key={d.iddepartement} value={d.iddepartement}>{d.libelle_departement}</option>
          ))}
        </SelectField>

        <SelectField
          label="Business Unit"
          help="Business Unit de l'employé"
          value={employe.business_unit_id || ''}
          onChange={(e) => setEmploye({ ...employe, business_unit_id: parseInt(e.target.value) || null })}
        >
          <option value="">Sélectionner</option>
          {businessUnits && businessUnits.map(bu => (
            <option key={bu.idbusiness_unit} value={bu.idbusiness_unit}>{bu.libelle}</option>
          ))}
        </SelectField>

        <SelectField
          label="Titre"
          help="Titre du poste de l'employé"
          value={employe.titre_id || ''}
          onChange={(e) => setEmploye({ ...employe, titre_id: parseInt(e.target.value) || null })}
        >
          <option value="">Sélectionner</option>
          {titres && titres.map(t => (
            <option key={t.idtitre} value={t.idtitre}>{t.nom_developpe}</option>
          ))}
        </SelectField>

        <SelectField
          label="Grade"
          help="Grade de l'employé"
          value={employe.grade_id || ''}
          onChange={(e) => setEmploye({ ...employe, grade_id: parseInt(e.target.value) || null })}
        >
          <option value="">Sélectionner</option>
          {grades && grades.map(g => (
            <option key={g.idgrade} value={g.idgrade}>{g.libelle}</option>
          ))}
        </SelectField>

        <SelectField
          label="Type de personnel"
          help="Catégorie de personnel (ex: MTN TOP 20)"
          value={employe.type_personnel_id || ''}
          onChange={(e) => setEmploye({ ...employe, type_personnel_id: parseInt(e.target.value) || null })}
        >
          <option value="">Sélectionner</option>
          {typesPersonnel && typesPersonnel.map(tp => (
            <option key={tp.idtype_personnel} value={tp.idtype_personnel}>{tp.libelle}</option>
          ))}
        </SelectField>

        <SelectField
          label="Campagne"
          help="Campagne actuelle de l'employé"
          value={employe.idcampagne || ''}
          onChange={(e) => setEmploye({ ...employe, idcampagne: parseInt(e.target.value) || 0 })}
          after={employe.idcampagne && !campagnesList?.find(c => c.idcampagne === employe.idcampagne) && (
            <p className="mt-1.5 flex items-start gap-1.5 text-xs text-amber-700">
              <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              La campagne actuelle n'est pas disponible dans cette filiale
            </p>
          )}
        >
          <option value="">Sélectionner</option>
          {campagnesList && campagnesList.map(c => (
            <option key={c.idcampagne} value={c.idcampagne}>{c.Libelle_campagne}</option>
          ))}
        </SelectField>

        <SelectField
          label="Marque"
          help="Marque de relation client"
          value={employe.marque_id || ''}
          onChange={(e) => setEmploye({ ...employe, marque_id: parseInt(e.target.value) || null })}
        >
          <option value="">Sélectionner</option>
          {marques && marques.map(m => (
            <option key={m.id} value={m.id}>{m.code} - {m.libelle}</option>
          ))}
        </SelectField>

        <TextField
          label="Emplacement"
          help="Lieu de travail (ex: DCH)"
          value={employe.emplacement || ''}
          onChange={(e) => setEmploye({ ...employe, emplacement: e.target.value })}
        />
      </Section>

      <Section title="Statut" description="Situation contractuelle et fiscale.">
        <SelectField
          label="Statut"
          help="Statut actuel de l'employé"
          value={employe.statut || ''}
          onChange={(e) => setEmploye({ ...employe, statut: e.target.value })}
        >
          <option value="">Sélectionner</option>
          <option value="Contractuel">Contractuel</option>
          <option value="Stagiaire">Stagiaire</option>
          <option value="Agent en formation">Agent en formation</option>
        </SelectField>

        <Segmented
          label="Direct/Indirect"
          help="1 = Direct, 0 = Indirect"
          value={employe.direct_indirect || 0}
          onSelect={(v) => setEmploye({ ...employe, direct_indirect: v })}
          options={[{ value: 0, label: 'Indirect' }, { value: 1, label: 'Direct' }]}
        />

        <Segmented
          label="Domiciliation"
          help="Domiciliation fiscale"
          value={employe.domiciliation || 'Non'}
          onSelect={(v) => setEmploye({ ...employe, domiciliation: v })}
          options={[{ value: 'Non', label: 'Non' }, { value: 'Oui', label: 'Oui' }]}
        />
      </Section>

      <Section title="Hiérarchie" description="Responsable direct et niveau supérieur.">
        <SelectField
          label="Manager hiérarchique"
          help="Responsable direct de l'employé"
          value={employe.manager_hierarchique_id || ''}
          onChange={(e) => setEmploye({ ...employe, manager_hierarchique_id: parseInt(e.target.value) || null })}
        >
          <option value="">Sélectionner</option>
          {managers && managers.map(m => (
            <option key={m.idpersonnel} value={m.idpersonnel}>{m.nom} {m.prenom} ({m.matricule})</option>
          ))}
        </SelectField>

        <TextField
          label="Fonction du manager"
          help="Fonction du manager hiérarchique"
          value={employe.fonction_manager || ''}
          onChange={(e) => setEmploye({ ...employe, fonction_manager: e.target.value })}
        />

        <SelectField
          label="N+2"
          help="Manager du manager (deuxième niveau hiérarchique)"
          value={employe.n2_id || ''}
          onChange={(e) => setEmploye({ ...employe, n2_id: parseInt(e.target.value) || null })}
        >
          <option value="">Sélectionner</option>
          {managers && managers.map(m => (
            <option key={m.idpersonnel} value={m.idpersonnel}>{m.nom} {m.prenom} ({m.matricule})</option>
          ))}
        </SelectField>
      </Section>
    </Panel>
  );
};

// Onglet Congés
const Conges = ({ employe }) => {
  const [loading, setLoading] = useState(true);
  const [demandes, setDemandes] = useState([]);

  useEffect(() => {
    loadCongeData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employe?.idpersonnel]);

  const loadCongeData = async () => {
    if (!employe?.idpersonnel) return;
    try {
      setLoading(true);
      const response = await http.get(`/api/conges?personnel_id=${employe.idpersonnel}`).catch(() => ({ data: [] }));
      const demandesArray = response.data?.data || response.data || [];
      setDemandes(Array.isArray(demandesArray) ? demandesArray : []);
    } catch (error) {
      console.error('Erreur chargement congés:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleValider = async (id) => {
    try {
      await http.put(`/api/conges/${id}`, { statut: 'valide' });
      loadCongeData();
      alert('Demande validée avec succès');
    } catch (error) {
      console.error('Erreur validation:', error);
      alert('Erreur lors de la validation');
    }
  };

  const handleRejeter = async (id) => {
    const motif = prompt('Motif du rejet :');
    if (motif) {
      try {
        await http.put(`/api/conges/${id}`, { statut: 'rejete', motif_rejet: motif });
        loadCongeData();
        alert('Demande rejetée');
      } catch (error) {
        console.error('Erreur rejet:', error);
        alert('Erreur lors du rejet');
      }
    }
  };

  const getEtatBadge = (etat) => {
    switch (etat) {
      case 0: return { text: 'En attente', className: 'bg-yellow-100 text-yellow-600' };
      case 1: return { text: 'Validé', className: 'bg-green-100 text-green-600' };
      case 2: return { text: 'Prévalidé', className: 'bg-blue-100 text-blue-600' };
      case 3: return { text: 'Rejeté', className: 'bg-red-100 text-red-600' };
      case 4: return { text: 'Annulé', className: 'bg-gray-100 text-gray-600' };
      default: return { text: 'Inconnu', className: 'bg-gray-100 text-gray-600' };
    }
  };

  if (loading) {
    return <LoadingBlock />;
  }

  return (
    <Panel title="Congés">
      <Section title="Solde actuel">
        <div className="col-span-full">
          <p className="text-sm text-slate-500">Solde de congé actuel</p>
          <p className="mt-1 text-4xl font-semibold tabular-nums text-slate-900">{employe.solde_conge || 0} jours</p>
        </div>
      </Section>

      <Section title="Demandes de congé" plain>
        {demandes.length === 0 ? (
          <EmptyState>Aucune demande de congé</EmptyState>
        ) : (
          <TableWrap>
            <thead>
              <tr>
                <Th>Date début</Th>
                <Th>Date fin</Th>
                <Th>Jours</Th>
                <Th>Motif</Th>
                <Th>Statut</Th>
                <Th>Actions</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 bg-white">
              {demandes.map((d) => {
                const badge = getEtatBadge(d.etat);
                return (
                  <tr key={d.idplanning_conge} className="hover:bg-slate-50">
                    <Td>{new Date(d.date_debut).toLocaleDateString('fr-FR')}</Td>
                    <Td>{new Date(d.date_fin).toLocaleDateString('fr-FR')}</Td>
                    <Td>{d.nombre_jour}</Td>
                    <Td>{d.motif || '-'}</Td>
                    <Td>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${badge.className}`}>
                        {badge.text}
                      </span>
                    </Td>
                    <Td>
                      {d.etat === 2 && (
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleValider(d.idplanning_conge)}
                            className="text-green-600 hover:text-green-700"
                            title="Valider"
                          >
                            <CheckCircle className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleRejeter(d.idplanning_conge)}
                            className="text-red-600 hover:text-red-700"
                            title="Rejeter"
                          >
                            <XCircle className="w-4 h-4" />
                          </button>
                        </div>
                      )}
                    </Td>
                  </tr>
                );
              })}
            </tbody>
          </TableWrap>
        )}
      </Section>
    </Panel>
  );
};

// Onglet Contrats
const Contrats = ({ employe }) => {
  const [loading, setLoading] = useState(true);
  const [contrats, setContrats] = useState([]);
  const [typesContrat, setTypesContrat] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedContrat, setSelectedContrat] = useState(null);
  const [formData, setFormData] = useState({
    personnel_id: employe?.idpersonnel,
    type_contrat_id: '',
    date_entree_contrat: '',
    date_fin_contrat: '',
    essai: 0,
    etat: 1,
    verification: 0,
    validation: 0
  });

  useEffect(() => {
    if (employe?.idpersonnel) {
      loadContrats();
      loadTypesContrat();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employe?.idpersonnel]);

  const loadContrats = async () => {
    if (!employe?.idpersonnel) return;
    try {
      setLoading(true);
      const response = await http.get(`/api/contrats/${employe.idpersonnel}`).catch(() => ({ data: [] }));
      setContrats(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Erreur chargement contrats:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadTypesContrat = async () => {
    try {
      const response = await http.get('/api/types-contrat').catch(() => ({ data: [] }));
      setTypesContrat(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Erreur chargement types contrat:', error);
    }
  };

  const handleAddContrat = async (e) => {
    e.preventDefault();
    try {
      await http.post('/api/contrats', {
        ...formData,
        personnel_id: employe.idpersonnel
      });
      setShowAddModal(false);
      setFormData({
        personnel_id: employe.idpersonnel,
        type_contrat_id: '',
        date_entree_contrat: '',
        date_fin_contrat: '',
        essai: 0,
        etat: 1,
        verification: 0,
        validation: 0
      });
      loadContrats();
    } catch (error) {
      console.error('Erreur ajout contrat:', error);
      alert('Erreur lors de l\'ajout du contrat');
    }
  };

  const handleEditContrat = async (e) => {
    e.preventDefault();
    try {
      await http.put(`/api/contrats/${selectedContrat.idpersonnel_contrat}`, formData);
      setShowEditModal(false);
      setSelectedContrat(null);
      loadContrats();
    } catch (error) {
      console.error('Erreur modification contrat:', error);
      alert('Erreur lors de la modification du contrat');
    }
  };

  const handleDeleteContrat = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce contrat ?')) return;
    try {
      console.log('[CONTRATS] Suppression contrat ID:', id);
      const response = await http.delete(`/api/contrats/${id}`);
      console.log('[CONTRATS] Réponse suppression:', response.data);
      console.log('[CONTRATS] Rechargement des contrats...');
      await loadContrats();
      console.log('[CONTRATS] Contrats après suppression:', contrats.length);
    } catch (error) {
      console.error('Erreur suppression contrat:', error);
      alert('Erreur lors de la suppression du contrat: ' + (error.response?.data?.error || error.message));
    }
  };

  const openEditModal = (contrat) => {
    setSelectedContrat(contrat);
    setFormData({
      personnel_id: employe.idpersonnel,
      type_contrat_id: contrat.type_contrat_id,
      date_entree_contrat: contrat.date_entree_contrat,
      date_fin_contrat: contrat.date_fin_contrat,
      essai: contrat.essai,
      etat: contrat.etat,
      verification: contrat.verification,
      validation: contrat.validation
    });
    setShowEditModal(true);
  };

  if (loading) {
    return <LoadingBlock />;
  }

  const contratActif = contrats.find(c => c.etat === 1);

  return (
    <>
      <Panel
        title="Contrats"
        action={(
          <button onClick={() => setShowAddModal(true)} className={btnPrimary}>
            <Plus className="h-4 w-4" />
            Ajouter un contrat
          </button>
        )}
      >
        <Section title="Contrat actuel">
          <div className="col-span-full">
            <StatList>
              <Stat label="Type de contrat actuel">{contratActif?.libelle_contrat || employe.type_contrat || '-'}</Stat>
              <Stat label="Date d'entrée contrat">{contratActif?.date_entree_contrat ? new Date(contratActif.date_entree_contrat).toLocaleDateString('fr-FR') : (employe.date_entree_contrat ? new Date(employe.date_entree_contrat).toLocaleDateString('fr-FR') : '-')}</Stat>
            </StatList>
          </div>
        </Section>

        <Section title="Historique des contrats" plain>
          {contrats.length === 0 ? (
            <EmptyState>Aucun contrat enregistré</EmptyState>
          ) : (
            <TableWrap>
              <thead>
                <tr>
                  <Th>Date début</Th>
                  <Th>Date fin</Th>
                  <Th>Type</Th>
                  <Th>Statut</Th>
                  <Th>Vérifié</Th>
                  <Th>Validé</Th>
                  <Th>Période d'essai</Th>
                  <Th>Actions</Th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 bg-white">
                {contrats.map((c, i) => (
                  <tr key={i} className="hover:bg-slate-50">
                    <Td>{c.date_entree_contrat ? new Date(c.date_entree_contrat).toLocaleDateString('fr-FR') : '-'}</Td>
                    <Td>{c.date_fin_contrat && c.date_fin_contrat !== '0000-00-00' ? new Date(c.date_fin_contrat).toLocaleDateString('fr-FR') : '-'}</Td>
                    <Td>{c.libelle_contrat || '-'}</Td>
                    <Td>
                      <Badge tone={c.etat === 1 ? 'green' : 'slate'}>
                        {c.etat === 1 ? 'Actif' : 'Inactif'}
                      </Badge>
                    </Td>
                    <Td>
                      <Badge tone={c.verification === 1 ? 'blue' : 'slate'}>
                        {c.verification === 1 ? 'Vérifié' : 'Non vérifié'}
                      </Badge>
                    </Td>
                    <Td>
                      <Badge tone={c.validation === 1 ? 'green' : 'slate'}>
                        {c.validation === 1 ? 'Validé' : 'Non validé'}
                      </Badge>
                    </Td>
                    <Td>{c.essai === 1 ? `Oui (${c.date_fin_essai ? new Date(c.date_fin_essai).toLocaleDateString('fr-FR') : '-'})` : 'Non'}</Td>
                    <Td>
                      <div className="flex gap-1">
                        <button
                          onClick={() => openEditModal(c)}
                          aria-label="Modifier le contrat"
                          title="Modifier"
                          className="rounded-md p-1.5 text-blue-600 transition hover:bg-blue-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-600/40"
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteContrat(c.idpersonnel_contrat)}
                          aria-label="Supprimer le contrat"
                          title="Supprimer"
                          className="rounded-md p-1.5 text-red-600 transition hover:bg-red-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-600/40"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </TableWrap>
          )}
        </Section>
      </Panel>

      {/* Modal Ajout */}
      {showAddModal && (
        <Modal title="Ajouter un contrat">
          <form onSubmit={handleAddContrat}>
            <ContratFields formData={formData} setFormData={setFormData} typesContrat={typesContrat} />
            <ModalActions>
              <button
                type="button"
                onClick={() => setShowAddModal(false)}
                className={btnSecondary}
              >
                Annuler
              </button>
              <button
                type="submit"
                className={btnPrimary}
              >
                Ajouter
              </button>
            </ModalActions>
          </form>
        </Modal>
      )}

      {/* Modal Modification */}
      {showEditModal && (
        <Modal title="Modifier le contrat">
          <form onSubmit={handleEditContrat}>
            <ContratFields formData={formData} setFormData={setFormData} typesContrat={typesContrat} suffix="-edit" showEtat />
            <ModalActions>
              <button
                type="button"
                onClick={() => setShowEditModal(false)}
                className={btnSecondary}
              >
                Annuler
              </button>
              <button
                type="submit"
                className={btnPrimary}
              >
                Modifier
              </button>
            </ModalActions>
          </form>
        </Modal>
      )}
    </>
  );
};

const MobiliteCarriere = ({ employe }) => {
  const [loading, setLoading] = useState(true);
  const [mouvements, setMouvements] = useState([]);
  const [demandes, setDemandes] = useState([]);
  const [entretiens, setEntretiens] = useState([]);

  useEffect(() => {
    loadMobiliteData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employe?.idpersonnel]);

  const loadMobiliteData = async () => {
    if (!employe?.idpersonnel) return;
    try {
      setLoading(true);
      const [mouvementsRes, demandesRes, entretiensRes] = await Promise.all([
        http.get(`/api/mouvements-carriere/personnel/${employe.idpersonnel}`).catch(() => ({ data: [] })),
        http.get(`/api/demandes-mobilite?personnel_id=${employe.idpersonnel}`).catch(() => ({ data: [] })),
        http.get(`/api/entretiens-carriere?personnel_id=${employe.idpersonnel}`).catch(() => ({ data: [] }))
      ]);
      setMouvements(Array.isArray(mouvementsRes.data) ? mouvementsRes.data : []);
      setDemandes(Array.isArray(demandesRes.data) ? demandesRes.data : []);
      setEntretiens(Array.isArray(entretiensRes.data) ? entretiensRes.data : []);
    } catch (error) {
      console.error('Erreur chargement mobilité:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatutBadge = (statut) => {
    switch (statut) {
      case 'en_attente': return { text: 'En attente', className: 'bg-yellow-100 text-yellow-600' };
      case 'valide_rh_source': return { text: 'Validé RH source', className: 'bg-orange-100 text-orange-600' };
      case 'valide_rh_destination': return { text: 'Validé RH destination', className: 'bg-purple-100 text-purple-600' };
      case 'accepte': return { text: 'Accepté', className: 'bg-green-100 text-green-600' };
      case 'refuse': return { text: 'Refusé', className: 'bg-red-100 text-red-600' };
      case 'en_cours': return { text: 'En cours', className: 'bg-blue-100 text-blue-600' };
      default: return { text: statut, className: 'bg-gray-100 text-gray-600' };
    }
  };

  const handleValider = async (id, role) => {
    const commentaire = prompt('Commentaire de validation (optionnel):');
    let statut = role === 'rh_source' ? 'valide_rh_source' : 'valide_rh_destination';
    
    if (role === 'rh_destination') {
      const choix = window.confirm('Voulez-vous ACCEPTER ou REFUSER cette demande ?\n\nOK = ACCEPTER\nAnnuler = REFUSER');
      statut = choix ? 'accepte' : 'refuse';
    }
    
    try {
      await http.put(`/api/demandes-mobilite/${id}/valider/${role}`, { 
        commentaire,
        statut
      });
      alert('Validation enregistrée avec succès');
      loadMobiliteData();
    } catch (error) {
      alert('Erreur lors de la validation: ' + (error.response?.data?.error || 'Erreur inconnue'));
    }
  };

  if (loading) {
    return <LoadingBlock />;
  }

  return (
    <Panel title="Mobilité & Carrière">
      <Section
        title="Demandes de mobilité"
        plain
        action={
          <button
            onClick={loadMobiliteData}
            className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-slate-700 bg-slate-100 rounded-md hover:bg-slate-200"
          >
            <RefreshCw className="h-3.5 w-3.5" />
            Actualiser
          </button>
        }
      >
        {demandes.length === 0 ? (
          <EmptyState>Aucune demande de mobilité</EmptyState>
        ) : (
          <TableWrap>
            <thead>
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Date demande</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Filiale destination</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Poste souhaité</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Motivation</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Statut</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-slate-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 bg-white">
              {demandes.map((d) => {
                const badge = getStatutBadge(d.statut);
                return (
                  <tr key={d.id} className="hover:bg-slate-50">
                    <td className="px-4 py-3 text-sm text-slate-500">{d.date_demande ? new Date(d.date_demande).toLocaleDateString('fr-FR') : '-'}</td>
                    <td className="px-4 py-3 text-sm text-slate-900">{d.filiale_destination_nom || '-'}</td>
                    <td className="px-4 py-3 text-sm text-slate-900">{d.titre_destination_nom_developpe || d.titre_destination_nom || '-'}</td>
                    <td className="px-4 py-3 text-sm text-slate-500">{d.motivation || '-'}</td>
                    <td className="px-4 py-3 text-sm">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${badge.className}`}>
                        {badge.text}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <div className="flex justify-center gap-2">
                        <button
                          onClick={() => handleValider(d.id, 'rh_source')}
                          className="text-purple-600 hover:text-purple-800"
                          title="Valider RH source"
                        >
                          <Shield className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleValider(d.id, 'rh_destination')}
                          className="text-orange-600 hover:text-orange-800"
                          title="Valider RH destination"
                        >
                          <Building2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </TableWrap>
        )}
      </Section>
    </Panel>
  );
};

const Absences = ({ employe }) => {
  const [loading, setLoading] = useState(true);
  const [absences, setAbsences] = useState([]);
  const [typeAbsenceList, setTypeAbsenceList] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editData, setEditData] = useState(null);
  const [formData, setFormData] = useState({
    personnel_id: employe?.idpersonnel || '',
    date_debut: '',
    date_fin: '',
    nbre_heures: '',
    type_absence_id: ''
  });

  // Calcul automatique du nombre d'heures
  useEffect(() => {
    if (formData.date_debut && formData.date_fin) {
      const debut = new Date(formData.date_debut);
      const fin = new Date(formData.date_fin);

      if (!isNaN(debut.getTime()) && !isNaN(fin.getTime()) && fin >= debut) {
        const diffTime = Math.abs(fin - debut);
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1; // +1 pour inclure le jour de début
        const heures = diffDays * 8; // 8 heures par jour
        setFormData(prev => ({ ...prev, nbre_heures: heures.toString() }));
      }
    }
  }, [formData.date_debut, formData.date_fin]);

  useEffect(() => {
    loadAbsences();
    loadTypeAbsence();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employe?.idpersonnel]);

  const loadAbsences = async () => {
    if (!employe?.idpersonnel) return;
    try {
      setLoading(true);
      const response = await http.get(`/api/absences?personnel_id=${employe.idpersonnel}`).catch(() => ({ data: [] }));
      setAbsences(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Erreur chargement absences:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadTypeAbsence = async () => {
    try {
      const response = await http.get('/api/type-absence').catch(() => ({ data: [] }));
      setTypeAbsenceList(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Erreur chargement types absence:', error);
    }
  };

  const getTypeAbsenceLabel = (typeId) => {
    const type = typeAbsenceList.find(t => t.idtype_absence === typeId);
    return type ? type.libelle : '-';
  };

  const getStatutBadge = (statut) => {
    switch (statut) {
      case 'en_attente_justification':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">En attente</span>;
      case 'en_attente_validation':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">À valider</span>;
      case 'justifiee':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">Justifiée</span>;
      case 'non_justifiee':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">Non justifiée</span>;
      case 'enregistree':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">Enregistrée</span>;
      case 'sanction':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">Sanction</span>;
      default:
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">{statut}</span>;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editData) {
        await http.put(`/api/absences/${editData.idabsence}`, formData);
      } else {
        await http.post('/api/absences', {
          ...formData,
          insertdate: new Date().toISOString().split('T')[0]
        });
      }
      setShowModal(false);
      setEditData(null);
      setFormData({
        personnel_id: employe.idpersonnel,
        date_debut: '',
        date_fin: '',
        nbre_heures: '',
        type_absence_id: ''
      });
      loadAbsences();
    } catch (error) {
      console.error('Erreur soumission:', error);
      alert('Erreur lors de la soumission');
    }
  };

  const handleEdit = (absence) => {
    setEditData(absence);
    setFormData({
      personnel_id: absence.personnel_id,
      date_debut: absence.date_debut,
      date_fin: absence.date_fin,
      nbre_heures: absence.nbre_heures,
      type_absence_id: absence.type_absence_id
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette absence ?')) return;
    try {
      await http.delete(`/api/absences/${id}`);
      loadAbsences();
    } catch (error) {
      console.error('Erreur suppression:', error);
      alert('Erreur lors de la suppression');
    }
  };

  const handleValider = async (id) => {
    try {
      await http.put(`/api/absences/${id}/valider`);
      loadAbsences();
      alert('Certificat validé avec succès');
    } catch (error) {
      console.error('Erreur validation:', error);
      alert('Erreur lors de la validation');
    }
  };

  const handleDownloadCertificat = async (certificatId, fileName) => {
    try {
      const response = await http.get(`/api/certificats/${certificatId}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Erreur téléchargement:', error);
      alert('Erreur lors du téléchargement');
    }
  };

  if (loading) {
    return <LoadingBlock />;
  }

  return (
    <Panel title="Absences" action={
      <button
        onClick={() => {
          setEditData(null);
          setFormData({
            personnel_id: employe.idpersonnel,
            date_debut: '',
            date_fin: '',
            nbre_heures: '',
            type_absence_id: ''
          });
          setShowModal(true);
        }}
        className="inline-flex items-center gap-1 text-xs font-medium text-blue-700 hover:text-blue-800"
      >
        <Plus className="h-3.5 w-3.5" />
        Nouvelle absence
      </button>
    }>
      <Section title="Historique des absences" plain>
        {absences.length === 0 ? (
          <EmptyState>Aucune absence enregistrée</EmptyState>
        ) : (
          <TableWrap>
            <thead>
              <tr>
                <Th>Date début</Th>
                <Th>Date fin</Th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Heures</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Type</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Statut</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 bg-white">
              {absences.map((a, i) => (
                <tr key={i} className="hover:bg-slate-50">
                  <Td>{a.date_debut ? new Date(a.date_debut).toLocaleDateString('fr-FR') : '-'}</Td>
                  <Td>{a.date_fin ? new Date(a.date_fin).toLocaleDateString('fr-FR') : '-'}</Td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-slate-500">{a.nbre_heures}h</td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-slate-500">{getTypeAbsenceLabel(a.type_absence_id)}</td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm">{getStatutBadge(a.statut)}</td>
                  <td className="px-4 py-3 whitespace-nowrap text-right text-sm">
                    <div className="flex justify-end gap-2">
                      {a.certificat_id && (
                        <button
                          onClick={() => handleDownloadCertificat(a.certificat_id, `certificat-${a.idabsence}.pdf`)}
                          className="text-green-600 hover:text-green-800"
                          title="Télécharger le certificat"
                        >
                          <Download className="h-4 w-4" />
                        </button>
                      )}
                      {a.statut === 'en_attente_validation' && (
                        <>
                          <button
                            onClick={() => handleValider(a.idabsence)}
                            className="text-green-600 hover:text-green-800"
                            title="Valider"
                          >
                            <Check className="h-4 w-4" />
                          </button>
                        </>
                      )}
                      {a.statut !== 'justifiee' && a.statut !== 'non_justifiee' && a.statut !== 'enregistree' && (
                        <>
                          <button
                            onClick={() => handleEdit(a)}
                            className="text-blue-600 hover:text-blue-800"
                            title="Modifier"
                          >
                            <Edit className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(a.idabsence)}
                            className="text-red-600 hover:text-red-800"
                            title="Supprimer"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </TableWrap>
        )}
      </Section>

      {/* Modal création/modification */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="w-full max-w-2xl rounded-2xl bg-white p-6 shadow-2xl">
            <div className="mb-4 flex items-center justify-between border-b border-slate-200 pb-4">
              <h3 className="text-lg font-bold text-slate-900">
                {editData ? 'Modifier une absence' : 'Signaler une absence'}
              </h3>
              <button
                onClick={() => {
                  setShowModal(false);
                  setEditData(null);
                  setFormData({
                    personnel_id: employe.idpersonnel,
                    date_debut: '',
                    date_fin: '',
                    nbre_heures: '',
                    type_absence_id: ''
                  });
                }}
                className="text-slate-400 hover:text-slate-600"
              >
                ✕
              </button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="space-y-4">
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-slate-700">
                    Employé
                  </label>
                  <input
                    type="text"
                    readOnly
                    value={`${employe.prenom} ${employe.nom}`}
                    className="w-full rounded-lg border border-slate-200 px-4 py-2 text-sm bg-slate-50"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="mb-1.5 block text-sm font-medium text-slate-700">
                      Date début
                    </label>
                    <input
                      type="date"
                      className="w-full rounded-lg border border-slate-200 px-4 py-2 text-sm focus:border-sky-500 focus:outline-none"
                      value={formData.date_debut}
                      onChange={(e) => setFormData({ ...formData, date_debut: e.target.value })}
                      required
                    />
                  </div>
                  <div>
                    <label className="mb-1.5 block text-sm font-medium text-slate-700">
                      Date fin
                    </label>
                    <input
                      type="date"
                      className="w-full rounded-lg border border-slate-200 px-4 py-2 text-sm focus:border-sky-500 focus:outline-none"
                      value={formData.date_fin}
                      onChange={(e) => setFormData({ ...formData, date_fin: e.target.value })}
                      required
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="mb-1.5 block text-sm font-medium text-slate-700">
                      Nombre d'heures
                    </label>
                    <input
                      type="number"
                      className="w-full rounded-lg border border-slate-200 px-4 py-2 text-sm focus:border-sky-500 focus:outline-none"
                      value={formData.nbre_heures}
                      onChange={(e) => setFormData({ ...formData, nbre_heures: e.target.value })}
                      required
                    />
                  </div>
                  <div>
                    <label className="mb-1.5 block text-sm font-medium text-slate-700">
                      Type d'absence
                    </label>
                    <select
                      className="w-full rounded-lg border border-slate-200 px-4 py-2 text-sm focus:border-sky-500 focus:outline-none"
                      value={formData.type_absence_id}
                      onChange={(e) => setFormData({ ...formData, type_absence_id: e.target.value })}
                      required
                    >
                      <option value="">Sélectionner</option>
                      {typeAbsenceList.map(t => (
                        <option key={t.idtype_absence} value={t.idtype_absence}>{t.libelle}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
              <div className="mt-6 flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    setEditData(null);
                    setFormData({
                      personnel_id: employe.idpersonnel,
                      date_debut: '',
                      date_fin: '',
                      nbre_heures: '',
                      type_absence_id: ''
                    });
                  }}
                  className="px-4 py-2 rounded-lg border border-slate-200 text-slate-700 hover:bg-slate-50"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 rounded-lg bg-sky-600 text-white hover:bg-sky-700"
                >
                  {editData ? 'Modifier' : 'Enregistrer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </Panel>
  );
};

const Paie = ({ employe }) => {
  const [activeSubTab, setActiveSubTab] = useState('infopaie');
  const [loading, setLoading] = useState(true);
  const [infopaie, setInfopaie] = useState(null);
  const [avances, setAvances] = useState([]);
  const [caisse, setCaisse] = useState(null);
  const [retenues, setRetenues] = useState([]);
  const [regularisations, setRegularisations] = useState([]);
  const [exclusions, setExclusions] = useState([]);
  const [showEditModal, setShowEditModal] = useState(false);
  const [banques, setBanques] = useState([]);
  const [paieEnCours, setPaieEnCours] = useState(null);
  const [avanceSearchTerm, setAvanceSearchTerm] = useState('');
  const [showAvanceModal, setShowAvanceModal] = useState(false);
  const [editingAvance, setEditingAvance] = useState(null);
  const [showDeleteAvanceModal, setShowDeleteAvanceModal] = useState(false);
  const [selectedAvance, setSelectedAvance] = useState(null);
  const [avanceFormData, setAvanceFormData] = useState({
    montant_avance: '',
    date_avance: ''
  });
  const [retenueSearchTerm, setRetenueSearchTerm] = useState('');
  const [showRetenueModal, setShowRetenueModal] = useState(false);
  const [editingRetenue, setEditingRetenue] = useState(null);
  const [showDeleteRetenueModal, setShowDeleteRetenueModal] = useState(false);
  const [selectedRetenue, setSelectedRetenue] = useState(null);
  const [retenueFormData, setRetenueFormData] = useState({
    montant_retenue: '',
    motif_retenue: '',
    date_retenue: ''
  });
  const [regularisationSearchTerm, setRegularisationSearchTerm] = useState('');
  const [showRegularisationModal, setShowRegularisationModal] = useState(false);
  const [editingRegularisation, setEditingRegularisation] = useState(null);
  const [showDeleteRegularisationModal, setShowDeleteRegularisationModal] = useState(false);
  const [selectedRegularisation, setSelectedRegularisation] = useState(null);
  const [regularisationFormData, setRegularisationFormData] = useState({
    montant_salaire_percu: '',
    montant: '',
    type: '0',
    commentaire: ''
  });
  const [exclusionSearchTerm, setExclusionSearchTerm] = useState('');
  const [showExclusionModal, setShowExclusionModal] = useState(false);
  const [editingExclusion, setEditingExclusion] = useState(null);
  const [exclusionFormData, setExclusionFormData] = useState({
    type_charge: 'cmu',
    type_exclusion: 'exempte',
    valeur_fixe: '',
    motif: '',
    actif: 1
  });
  const [formData, setFormData] = useState({
    type_paiement: '',
    banque: '',
    numero_compte: '',
    prime_deplacement: '',
    prime_de_astreinte: '',
    prime_de_perdiem: '',
    prime_de_caisse: '',
    prime_de_responsabilite: '',
    prime_de_eloignement: ''
  });

  const TYPE_PAIEMENT_OPTIONS = ['Virement', 'Chèque', 'Espèce', 'Mobile Money'];

  useEffect(() => {
    loadBanques();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employe?.filiale_id]);

  useEffect(() => {
    if (activeSubTab === 'infopaie') {
      loadInfopaie();
    } else if (activeSubTab === 'avances') {
      loadAvances();
    } else if (activeSubTab === 'caisse') {
      loadCaisse();
    } else if (activeSubTab === 'retenues') {
      loadRetenues();
    } else if (activeSubTab === 'regularisations') {
      loadRegularisations();
    } else if (activeSubTab === 'exclusions') {
      loadExclusions();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSubTab, employe?.idpersonnel, banques]);

  const loadInfopaie = async () => {
    if (!employe?.idpersonnel) return;
    try {
      setLoading(true);
      const response = await http.get(`/api/personnel-infopaie/${employe.idpersonnel}`);
      setInfopaie(response.data || null);
    } catch (error) {
      console.error('Erreur chargement infopaie:', error);
      setInfopaie(null);
    } finally {
      setLoading(false);
    }
  };

  const loadBanques = async () => {
    try {
      if (!employe?.filiale_id) {
        setBanques([]);
        return;
      }
      const response = await http.get(`/api/banques?filiale_id=${employe.filiale_id}`);
      console.log('[BANQUES] Loaded:', response.data);
      setBanques(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Erreur chargement banques:', error);
      setBanques([]);
    }
  };

  const handleEditInfopaie = () => {
    if (infopaie) {
      setFormData({
        type_paiement: infopaie.type_paiement || '',
        banque: infopaie.banque || '',
        numero_compte: infopaie.numero_compte || '',
        prime_deplacement: infopaie.prime_deplacement || '',
        prime_de_astreinte: infopaie.prime_de_astreinte || '',
        prime_de_perdiem: infopaie.prime_de_perdiem || '',
        prime_de_caisse: infopaie.prime_de_caisse || '',
        prime_de_responsabilite: infopaie.prime_de_responsabilite || '',
        prime_de_eloignement: infopaie.prime_de_eloignement || ''
      });
      setShowEditModal(true);
    }
  };

  const handleSaveInfopaie = async (e) => {
    e.preventDefault();
    try {
      await http.post(`/api/personnel-infopaie/${employe.idpersonnel}`, formData);
      setShowEditModal(false);
      loadInfopaie();
      alert('Informations de paie mises à jour avec succès');
    } catch (error) {
      console.error('Erreur mise à jour infopaie:', error);
      alert('Erreur lors de la mise à jour');
    }
  };

  const loadAvances = async () => {
    if (!employe?.idpersonnel) return;
    try {
      setLoading(true);

      // Récupérer la période de paie en cours pour la filiale de l'employé
      const paieRes = await http.get(`/api/paies/encours?filiale_id=${employe.filiale_id}`).catch(() => ({ data: null }));
      console.log('[AVANCES] Paie en cours:', paieRes.data);
      setPaieEnCours(paieRes.data);

      if (paieRes.data) {
        const response = await http.get(`/api/avances?paie_id=${paieRes.data.idpaie}`);
        const allAvances = Array.isArray(response.data) ? response.data : [];
        console.log('[AVANCES] All avances:', allAvances);
        const filteredAvances = allAvances.filter(a => a.personnel_id === employe.idpersonnel);
        console.log('[AVANCES] Filtered avances:', filteredAvances);
        setAvances(filteredAvances);
      } else {
        setAvances([]);
      }
    } catch (error) {
      console.error('Erreur chargement avances:', error);
      setAvances([]);
    } finally {
      setLoading(false);
    }
  };

  const loadCaisse = async () => {
    if (!employe?.idpersonnel) return;
    try {
      setLoading(true);
      const response = await http.get(`/api/caisse-sociale?filiale_id=${employe.filiale_id}`);
      const allCaisses = Array.isArray(response.data) ? response.data : [];
      const employeCaisse = allCaisses.find(c => c.idpersonnel === employe.idpersonnel);
      setCaisse(employeCaisse || null);
    } catch (error) {
      console.error('Erreur chargement caisse:', error);
      setCaisse(null);
    } finally {
      setLoading(false);
    }
  };

  const handleAddCaisse = () => {
    setCaisseFormData({
      approbation: true
    });
    setShowCaisseModal(true);
  };

  const handleDeleteCaisse = async (caisseItem) => {
    if (!window.confirm('Êtes-vous sûr de vouloir retirer cet employé de la caisse sociale ?')) return;
    try {
      await http.delete(`/api/caisse-sociale/${caisseItem.id}`);
      alert('Employé retiré de la caisse sociale avec succès');
      loadCaisse();
    } catch (error) {
      alert('Erreur lors de la suppression: ' + (error.response?.data?.error || 'Erreur inconnue'));
    }
  };

  const handleCaisseSubmit = async (e) => {
    e.preventDefault();
    try {
      await http.post('/api/caisse-sociale', {
        personnel_id: employe.idpersonnel,
        approbation: caisseFormData.approbation,
        filiale_id: employe.filiale_id
      });
      alert('Employé ajouté à la caisse sociale avec succès');
      setShowCaisseModal(false);
      loadCaisse();
    } catch (error) {
      alert('Erreur: ' + (error.response?.data?.error || 'Erreur inconnue'));
    }
  };

  const loadRetenues = async () => {
    if (!employe?.idpersonnel) return;
    try {
      setLoading(true);

      // Récupérer la période de paie en cours pour la filiale de l'employé
      const paieRes = await http.get(`/api/paies/encours?filiale_id=${employe.filiale_id}`).catch(() => ({ data: null }));
      setPaieEnCours(paieRes.data);

      if (paieRes.data) {
        const response = await http.get(`/api/retenues?paie_id=${paieRes.data.idpaie}`);
        const allRetenues = Array.isArray(response.data) ? response.data : [];
        setRetenues(allRetenues.filter(r => r.personnel_id === employe.idpersonnel));
      } else {
        setRetenues([]);
      }
    } catch (error) {
      console.error('Erreur chargement retenues:', error);
      setRetenues([]);
    } finally {
      setLoading(false);
    }
  };

  const loadRegularisations = async () => {
    if (!employe?.idpersonnel) return;
    try {
      setLoading(true);

      // Récupérer la période de paie en cours pour la filiale de l'employé
      const paieRes = await http.get(`/api/paies/encours?filiale_id=${employe.filiale_id}`).catch(() => ({ data: null }));
      setPaieEnCours(paieRes.data);

      if (paieRes.data) {
        const response = await http.get(`/api/regularisations?paie_id=${paieRes.data.idpaie}`);
        const allRegularisations = Array.isArray(response.data) ? response.data : [];
        setRegularisations(allRegularisations.filter(r => r.personnel_id === employe.idpersonnel));
      } else {
        setRegularisations([]);
      }
    } catch (error) {
      console.error('Erreur chargement regularisations:', error);
      setRegularisations([]);
    } finally {
      setLoading(false);
    }
  };

  const loadExclusions = async () => {
    if (!employe?.idpersonnel) return;
    try {
      setLoading(true);
      const response = await http.get(`/api/exclusions-charges?filiale_id=${employe.filiale_id}`);
      const allExclusions = Array.isArray(response.data) ? response.data : [];
      setExclusions(allExclusions.filter(e => e.personnel_id === employe.idpersonnel));
    } catch (error) {
      console.error('Erreur chargement exclusions:', error);
      setExclusions([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAddAvance = () => {
    if (!paieEnCours) {
      alert('Merci d\'ouvrir une nouvelle période de paie');
      return;
    }
    setEditingAvance(null);
    setAvanceFormData({
      montant_avance: '',
      date_avance: ''
    });
    setShowAvanceModal(true);
  };

  const handleEditAvance = (avance) => {
    setEditingAvance(avance);
    setAvanceFormData({
      montant_avance: avance.montant_avance,
      date_avance: avance.date_avance
    });
    setShowAvanceModal(true);
  };

  const handleDeleteAvance = (avance) => {
    setSelectedAvance(avance);
    setShowDeleteAvanceModal(true);
  };

  const handleDeleteAvanceConfirm = async () => {
    try {
      await http.delete(`/api/avances/${selectedAvance.idpersonnel_avance}`);
      alert('Avance sur salaire supprimée avec succès');
      setShowDeleteAvanceModal(false);
      loadAvances();
    } catch (error) {
      console.error('Erreur suppression:', error);
      alert('Erreur lors de la suppression');
    }
  };

  const handleAvanceSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingAvance) {
        await http.put(`/api/avances/${editingAvance.idpersonnel_avance}`, {
          personnel_id: employe.idpersonnel,
          ...avanceFormData
        });
        alert('Avance sur salaire modifiée avec succès');
      } else {
        await http.post('/api/avances', {
          personnel_id: employe.idpersonnel,
          montant_avance: avanceFormData.montant_avance,
          date_avance: avanceFormData.date_avance,
          paie_id: paieEnCours.idpaie
        });
        alert('Avance sur salaire créée avec succès');
      }
      setShowAvanceModal(false);
      setEditingAvance(null);
      loadAvances();
    } catch (error) {
      console.error('Erreur soumission:', error);
      alert('Erreur lors de la soumission');
    }
  };

  const handleAddRetenue = () => {
    if (!paieEnCours) {
      alert('Merci d\'ouvrir une nouvelle période de paie');
      return;
    }
    setEditingRetenue(null);
    setRetenueFormData({
      montant_retenue: '',
      motif_retenue: '',
      date_retenue: ''
    });
    setShowRetenueModal(true);
  };

  const handleEditRetenue = (retenue) => {
    setEditingRetenue(retenue);
    setRetenueFormData({
      montant_retenue: retenue.montant_retenue,
      motif_retenue: retenue.motif_retenue,
      date_retenue: retenue.date_retenue
    });
    setShowRetenueModal(true);
  };

  const handleDeleteRetenue = (retenue) => {
    setSelectedRetenue(retenue);
    setShowDeleteRetenueModal(true);
  };

  const handleDeleteRetenueConfirm = async () => {
    try {
      await http.delete(`/api/retenues/${selectedRetenue.idpersonnel_retenue}`);
      alert('Retenue sur salaire supprimée avec succès');
      setShowDeleteRetenueModal(false);
      loadRetenues();
    } catch (error) {
      console.error('Erreur suppression:', error);
      alert('Erreur lors de la suppression');
    }
  };

  const handleRetenueSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingRetenue) {
        await http.put(`/api/retenues/${editingRetenue.idpersonnel_retenue}`, {
          personnel_id: employe.idpersonnel,
          ...retenueFormData
        });
        alert('Retenue sur salaire modifiée avec succès');
      } else {
        await http.post('/api/retenues', {
          personnel_id: employe.idpersonnel,
          montant_retenue: retenueFormData.montant_retenue,
          motif_retenue: retenueFormData.motif_retenue,
          date_retenue: retenueFormData.date_retenue,
          paie_id: paieEnCours.idpaie
        });
        alert('Retenue sur salaire créée avec succès');
      }
      setShowRetenueModal(false);
      setEditingRetenue(null);
      loadRetenues();
    } catch (error) {
      console.error('Erreur soumission:', error);
      alert('Erreur lors de la soumission');
    }
  };

  const handleAddRegularisation = () => {
    if (!paieEnCours) {
      alert('Merci d\'ouvrir une nouvelle période de paie');
      return;
    }
    setEditingRegularisation(null);
    setRegularisationFormData({
      montant_salaire_percu: '',
      montant: '',
      type: '0',
      commentaire: ''
    });
    setShowRegularisationModal(true);
  };

  const handleEditRegularisation = (reg) => {
    setEditingRegularisation(reg);
    setRegularisationFormData({
      montant_salaire_percu: reg.montant_salaire_percu,
      montant: reg.montant,
      type: reg.type.toString(),
      commentaire: reg.commentaire
    });
    setShowRegularisationModal(true);
  };

  const handleDeleteRegularisation = (reg) => {
    setSelectedRegularisation(reg);
    setShowDeleteRegularisationModal(true);
  };

  const handleDeleteRegularisationConfirm = async () => {
    try {
      await http.delete(`/api/regularisations/${selectedRegularisation.idpersonnel_regularisation}`);
      alert('Régularisation supprimée avec succès');
      setShowDeleteRegularisationModal(false);
      loadRegularisations();
    } catch (error) {
      console.error('Erreur suppression:', error);
      alert('Erreur lors de la suppression');
    }
  };

  const handleRegularisationSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingRegularisation) {
        await http.put(`/api/regularisations/${editingRegularisation.idpersonnel_regularisation}`, {
          personnel_id: employe.idpersonnel,
          ...regularisationFormData,
          type: parseInt(regularisationFormData.type)
        });
        alert('Régularisation modifiée avec succès');
      } else {
        await http.post('/api/regularisations', {
          personnel_id: employe.idpersonnel,
          montant_salaire_percu: regularisationFormData.montant_salaire_percu,
          montant: regularisationFormData.montant,
          type: parseInt(regularisationFormData.type),
          commentaire: regularisationFormData.commentaire,
          paie_id: paieEnCours.idpaie
        });
        alert('Régularisation créée avec succès');
      }
      setShowRegularisationModal(false);
      setEditingRegularisation(null);
      loadRegularisations();
    } catch (error) {
      console.error('Erreur soumission:', error);
      alert('Erreur lors de la soumission');
    }
  };

  const handleAddExclusion = () => {
    setEditingExclusion(null);
    setExclusionFormData({
      type_charge: 'cmu',
      type_exclusion: 'exempte',
      valeur_fixe: '',
      motif: '',
      actif: 1
    });
    setShowExclusionModal(true);
  };

  const handleEditExclusion = (exclusion) => {
    setEditingExclusion(exclusion);
    setExclusionFormData({
      type_charge: exclusion.type_charge,
      type_exclusion: exclusion.type_exclusion,
      valeur_fixe: exclusion.valeur_fixe,
      motif: exclusion.motif,
      actif: exclusion.actif
    });
    setShowExclusionModal(true);
  };

  const handleDeleteExclusion = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette exclusion ?')) return;
    try {
      await http.delete(`/api/exclusions-charges/${id}`);
      loadExclusions();
    } catch (error) {
      alert('Erreur lors de la suppression: ' + (error.response?.data?.error || 'Erreur inconnue'));
    }
  };

  const handleExclusionSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...exclusionFormData,
        personnel_id: employe.idpersonnel,
        filiale_id: employe.filiale_id
      };
      
      if (editingExclusion) {
        await http.put(`/api/exclusions-charges/${editingExclusion.id}`, data);
        alert('Exclusion modifiée avec succès');
      } else {
        await http.post('/api/exclusions-charges', data);
        alert('Exclusion créée avec succès');
      }
      setShowExclusionModal(false);
      setEditingExclusion(null);
      loadExclusions();
    } catch (error) {
      alert('Erreur: ' + (error.response?.data?.error || 'Erreur inconnue'));
    }
  };

  const getTypeChargeLabel = (type) => {
    const labels = {
      cmu: 'CMU',
      cnps: 'CNPS',
      impot_salaire: 'Impôt salaire',
      igr: 'IGR',
      prime_anciennete: 'Prime ancienneté',
      prime_variable: 'Prime variable'
    };
    return labels[type] || type;
  };

  const getTypeExclusionLabel = (type) => {
    const labels = {
      exempte: 'Exempté (fixe)',
      zero: 'Toujours 0',
      fixe: 'Montant fixe'
    };
    return labels[type] || type;
  };

  const [showCaisseModal, setShowCaisseModal] = useState(false);
  const [caisseFormData, setCaisseFormData] = useState({
    approbation: true
  });

  const subTabs = [
    { id: 'infopaie', label: 'Informations paie' },
    { id: 'avances', label: 'Avances sur salaire' },
    { id: 'caisse', label: 'Caisse sociale' },
    { id: 'retenues', label: 'Retenues' },
    { id: 'regularisations', label: 'Régularisations' },
    { id: 'exclusions', label: 'Exclusions de charges' }
  ];

  if (loading) {
    return <LoadingBlock />;
  }

  return (
    <Panel title="Paie">
      {/* Sous-onglets */}
      <div className="mb-4 flex gap-1 border-b border-slate-200">
        {subTabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveSubTab(tab.id)}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeSubTab === tab.id
                ? 'border-blue-600 text-blue-700'
                : 'border-transparent text-slate-600 hover:text-slate-900'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Contenu des sous-onglets */}
      {activeSubTab === 'infopaie' && (
        <Section 
          title="Informations de paie" 
          plain
          action={infopaie && (
            <button
              onClick={handleEditInfopaie}
              className="inline-flex items-center gap-1 text-xs font-medium text-blue-700 hover:text-blue-800"
            >
              <Edit className="h-3.5 w-3.5" />
              Modifier
            </button>
          )}
        >
          {!infopaie ? (
            <EmptyState
              icon={DollarSign}
              title="Aucune information de paie"
              hint="Aucune donnée de paie n'est enregistrée pour cet employé"
            />
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div className="p-4 rounded-lg bg-slate-50">
                <p className="text-xs text-slate-500 mb-1">Type de paiement</p>
                <p className="text-sm font-medium text-slate-900">{infopaie.type_paiement || '-'}</p>
              </div>
              <div className="p-4 rounded-lg bg-slate-50">
                <p className="text-xs text-slate-500 mb-1">Banque</p>
                <p className="text-sm font-medium text-slate-900">{banques.find(b => String(b.idbanque) === String(infopaie.banque))?.libelle_banque || infopaie.banque || '-'}</p>
              </div>
              <div className="p-4 rounded-lg bg-slate-50">
                <p className="text-xs text-slate-500 mb-1">Numéro de compte</p>
                <p className="text-sm font-medium text-slate-900">{infopaie.numero_compte || '-'}</p>
              </div>
              <div className="p-4 rounded-lg bg-slate-50">
                <p className="text-xs text-slate-500 mb-1">Prime de déplacement</p>
                <p className="text-sm font-medium text-slate-900">{infopaie.prime_deplacement || '-'}</p>
              </div>
              <div className="p-4 rounded-lg bg-slate-50">
                <p className="text-xs text-slate-500 mb-1">Prime d'astreinte</p>
                <p className="text-sm font-medium text-slate-900">{infopaie.prime_de_astreinte || '-'}</p>
              </div>
              <div className="p-4 rounded-lg bg-slate-50">
                <p className="text-xs text-slate-500 mb-1">Prime de perdiem</p>
                <p className="text-sm font-medium text-slate-900">{infopaie.prime_de_perdiem || '-'}</p>
              </div>
              <div className="p-4 rounded-lg bg-slate-50">
                <p className="text-xs text-slate-500 mb-1">Prime de caisse</p>
                <p className="text-sm font-medium text-slate-900">{infopaie.prime_de_caisse || '-'}</p>
              </div>
              <div className="p-4 rounded-lg bg-slate-50">
                <p className="text-xs text-slate-500 mb-1">Prime de responsabilité</p>
                <p className="text-sm font-medium text-slate-900">{infopaie.prime_de_responsabilite || '-'}</p>
              </div>
              <div className="p-4 rounded-lg bg-slate-50">
                <p className="text-xs text-slate-500 mb-1">Prime d'éloignement</p>
                <p className="text-sm font-medium text-slate-900">{infopaie.prime_de_eloignement || '-'}</p>
              </div>
            </div>
          )}
        </Section>
      )}

      {activeSubTab === 'avances' && (
        <Section
          title="Avances sur salaire"
          plain
          action={
            <div className="flex gap-2 items-center">
              <button
                onClick={loadAvances}
                className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-slate-700 bg-slate-100 rounded-md hover:bg-slate-200"
              >
                <RefreshCw className="h-3.5 w-3.5" />
                Actualiser
              </button>
              <button
                onClick={handleAddAvance}
                disabled={!paieEnCours}
                title={!paieEnCours ? "Aucune période de paie en cours" : ""}
                className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Plus className="h-3.5 w-3.5" />
                Ajouter
              </button>
              {(() => {
                console.log('[RENDER] paieEnCours:', paieEnCours);
                return !paieEnCours ? (
                  <span className="text-xs text-slate-500 ml-2">Période de paie requise</span>
                ) : null;
              })()}
            </div>
          }
        >
          {!paieEnCours && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
              <p className="text-yellow-800 text-sm">Merci d'ouvrir une nouvelle période de paie pour gérer les avances sur salaire.</p>
            </div>
          )}
          {avances.length === 0 ? (
            <EmptyState
              icon={DollarSign}
              title="Aucune avance sur salaire"
              hint="Cet employé n'a pas encore d'avances sur salaire"
            />
          ) : (
            <div>
              <div className="mb-4 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                <input
                  type="text"
                  placeholder="Rechercher..."
                  value={avanceSearchTerm}
                  onChange={(e) => setAvanceSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                />
              </div>
              <TableWrap>
                <thead>
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Date</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Montant</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Période</th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-slate-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 bg-white">
                  {avances
                    .filter(a =>
                      a.date_avance?.toLowerCase().includes(avanceSearchTerm.toLowerCase()) ||
                      a.montant_avance?.toString().includes(avanceSearchTerm)
                    )
                    .map((avance) => (
                    <tr key={avance.idpersonnel_avance} className="hover:bg-slate-50">
                      <Td>{avance.date_avance ? new Date(avance.date_avance).toLocaleDateString('fr-FR') : '-'}</Td>
                      <td className="px-4 py-3 text-sm text-slate-900">{avance.montant_avance || '-'}</td>
                      <td className="px-4 py-3 text-sm text-slate-500">
                        {(() => {
                          console.log('[RENDER AVANCE] avance:', avance);
                          return avance.libelle_paie || (avance.date_debut && avance.date_fin ? `${new Date(avance.date_debut).toLocaleDateString('fr-FR')} - ${new Date(avance.date_fin).toLocaleDateString('fr-FR')}` : '-');
                        })()}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <div className="flex justify-center gap-2">
                          <button
                            onClick={() => handleEditAvance(avance)}
                            className="text-blue-600 hover:text-blue-800"
                            title="Modifier"
                          >
                            <Edit className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteAvance(avance)}
                            className="text-red-600 hover:text-red-800"
                            title="Supprimer"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-slate-50 border-t border-slate-200">
                  <tr>
                    <td colSpan="1" className="px-4 py-3"></td>
                    <td className="px-4 py-3 font-bold text-slate-900 text-right">
                      {avances.reduce((sum, a) => sum + parseFloat(a.montant_avance || 0), 0).toLocaleString()} FCFA
                    </td>
                    <td colSpan="2" className="px-4 py-3"></td>
                  </tr>
                </tfoot>
              </TableWrap>
            </div>
          )}
        </Section>
      )}

      {activeSubTab === 'caisse' && (
        <Section
          title="Caisse sociale"
          plain
          action={
            <div className="flex gap-2 items-center">
              <button
                onClick={loadCaisse}
                className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-slate-700 bg-slate-100 rounded-md hover:bg-slate-200"
              >
                <RefreshCw className="h-3.5 w-3.5" />
                Actualiser
              </button>
              {!caisse ? (
                <button
                  onClick={handleAddCaisse}
                  className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
                >
                  <Plus className="h-3.5 w-3.5" />
                  Inscrire
                </button>
              ) : (
                <button
                  onClick={() => handleDeleteCaisse(caisse)}
                  className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-white bg-red-600 rounded-md hover:bg-red-700"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                  Retirer
                </button>
              )}
            </div>
          }
        >
          {!caisse ? (
            <EmptyState
              icon={Folder}
              title="Non inscrit à la caisse sociale"
              hint="Cet employé n'est pas inscrit à la caisse sociale"
            />
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="p-4 rounded-lg bg-slate-50">
                <p className="text-xs text-slate-500 mb-1">Date de souscription</p>
                <p className="text-sm font-medium text-slate-900">{caisse.date_souscription ? new Date(caisse.date_souscription).toLocaleDateString('fr-FR') : '-'}</p>
              </div>
              <div className="p-4 rounded-lg bg-slate-50">
                <p className="text-xs text-slate-500 mb-1">Statut</p>
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                  caisse.approbation === 1 ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                }`}>
                  {caisse.approbation === 1 ? 'Approuvé' : 'En attente'}
                </span>
              </div>
            </div>
          )}
        </Section>
      )}

      {activeSubTab === 'retenues' && (
        <Section
          title="Retenues sur salaire"
          plain
          action={
            <div className="flex gap-2 items-center">
              <button
                onClick={loadRetenues}
                className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-slate-700 bg-slate-100 rounded-md hover:bg-slate-200"
              >
                <RefreshCw className="h-3.5 w-3.5" />
                Actualiser
              </button>
              <button
                onClick={handleAddRetenue}
                disabled={!paieEnCours}
                title={!paieEnCours ? "Aucune période de paie en cours" : ""}
                className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Plus className="h-3.5 w-3.5" />
                Ajouter
              </button>
              {!paieEnCours && (
                <span className="text-xs text-slate-500 ml-2">Période de paie requise</span>
              )}
            </div>
          }
        >
          {!paieEnCours && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
              <p className="text-yellow-800 text-sm">Merci d'ouvrir une nouvelle période de paie pour gérer les retenues sur salaire.</p>
            </div>
          )}
          {retenues.length === 0 ? (
            <EmptyState
              icon={DollarSign}
              title="Aucune retenue sur salaire"
              hint="Cet employé n'a pas de retenues sur salaire"
            />
          ) : (
            <div>
              <div className="mb-4 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                <input
                  type="text"
                  placeholder="Rechercher..."
                  value={retenueSearchTerm}
                  onChange={(e) => setRetenueSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                />
              </div>
              <TableWrap>
                <thead>
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Date</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Montant</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Période</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Motif</th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-slate-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 bg-white">
                  {retenues
                    .filter(r =>
                      r.date_retenue?.toLowerCase().includes(retenueSearchTerm.toLowerCase()) ||
                      r.montant_retenue?.toString().includes(retenueSearchTerm) ||
                      r.motif_retenue?.toLowerCase().includes(retenueSearchTerm.toLowerCase())
                    )
                    .map((retenue) => (
                    <tr key={retenue.idpersonnel_retenue} className="hover:bg-slate-50">
                      <Td>{retenue.date_retenue ? new Date(retenue.date_retenue).toLocaleDateString('fr-FR') : '-'}</Td>
                      <td className="px-4 py-3 text-sm text-slate-900">{retenue.montant_retenue || '-'}</td>
                      <td className="px-4 py-3 text-sm text-slate-500">{retenue.libelle_paie || '-'}</td>
                      <td className="px-4 py-3 text-sm text-slate-500">{retenue.motif_retenue || '-'}</td>
                      <td className="px-4 py-3 text-center">
                        <div className="flex justify-center gap-2">
                          <button
                            onClick={() => handleEditRetenue(retenue)}
                            className="text-blue-600 hover:text-blue-800"
                            title="Modifier"
                          >
                            <Edit className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteRetenue(retenue)}
                            className="text-red-600 hover:text-red-800"
                            title="Supprimer"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-slate-50 border-t border-slate-200">
                  <tr>
                    <td colSpan="1" className="px-4 py-3"></td>
                    <td className="px-4 py-3 font-bold text-slate-900 text-right">
                      {retenues.reduce((sum, r) => sum + parseFloat(r.montant_retenue || 0), 0).toLocaleString()} FCFA
                    </td>
                    <td colSpan="3" className="px-4 py-3"></td>
                  </tr>
                </tfoot>
              </TableWrap>
            </div>
          )}
        </Section>
      )}

      {activeSubTab === 'regularisations' && (
        <Section
          title="Régularisations"
          plain
          action={
            <div className="flex gap-2 items-center">
              <button
                onClick={loadRegularisations}
                className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-slate-700 bg-slate-100 rounded-md hover:bg-slate-200"
              >
                <RefreshCw className="h-3.5 w-3.5" />
                Actualiser
              </button>
              <button
                onClick={handleAddRegularisation}
                disabled={!paieEnCours}
                title={!paieEnCours ? "Aucune période de paie en cours" : ""}
                className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Plus className="h-3.5 w-3.5" />
                Ajouter
              </button>
            </div>
          }
        >
          {!paieEnCours && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4 flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 text-yellow-600 mt-0.5 flex-shrink-0" />
              <p className="text-yellow-800 text-sm">Merci d'ouvrir une nouvelle période de paie pour gérer les régularisations.</p>
            </div>
          )}
          {regularisations.length === 0 ? (
            <EmptyState
              icon={DollarSign}
              title="Aucune régularisation"
              hint="Cet employé n'a pas de régularisations"
            />
          ) : (
            <div>
              <div className="mb-4 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                <input
                  type="text"
                  placeholder="Rechercher..."
                  value={regularisationSearchTerm}
                  onChange={(e) => setRegularisationSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                />
              </div>
              <TableWrap>
                <thead>
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Période</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Salaire perçu</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Type</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Montant</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Commentaire</th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-slate-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 bg-white">
                  {regularisations
                    .filter(r =>
                      r.libelle_paie?.toLowerCase().includes(regularisationSearchTerm.toLowerCase()) ||
                      r.montant_salaire_percu?.toString().includes(regularisationSearchTerm) ||
                      r.montant?.toString().includes(regularisationSearchTerm) ||
                      r.commentaire?.toLowerCase().includes(regularisationSearchTerm.toLowerCase())
                    )
                    .map((reg) => (
                    <tr key={reg.idpersonnel_regularisation} className="hover:bg-slate-50">
                      <td className="px-4 py-3 text-sm text-slate-500">{reg.libelle_paie || '-'}</td>
                      <td className="px-4 py-3 text-sm text-slate-900">{reg.montant_salaire_percu || '-'}</td>
                      <td className="px-4 py-3 text-sm text-slate-600">
                        {reg.type === 0 ? 'Trop perçu' : 'Moins perçu'}
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-900">{reg.montant || '-'}</td>
                      <td className="px-4 py-3 text-sm text-slate-500">{reg.commentaire || '-'}</td>
                      <td className="px-4 py-3 text-center">
                        <div className="flex justify-center gap-2">
                          <button
                            onClick={() => handleEditRegularisation(reg)}
                            className="text-blue-600 hover:text-blue-800"
                            title="Modifier"
                          >
                            <Edit className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteRegularisation(reg)}
                            className="text-red-600 hover:text-red-800"
                            title="Supprimer"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-slate-50 border-t border-slate-200">
                  <tr>
                    <td colSpan="3" className="px-4 py-3"></td>
                    <td className="px-4 py-3 font-bold text-slate-900 text-right">
                      {regularisations.reduce((sum, r) => sum + parseFloat(r.montant || 0), 0).toLocaleString()} FCFA
                    </td>
                    <td colSpan="2" className="px-4 py-3"></td>
                  </tr>
                </tfoot>
              </TableWrap>
            </div>
          )}
        </Section>
      )}

      {activeSubTab === 'exclusions' && (
        <Section
          title="Exclusions de charges"
          plain
          action={
            <div className="flex gap-2 items-center">
              <button
                onClick={loadExclusions}
                className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-slate-700 bg-slate-100 rounded-md hover:bg-slate-200"
              >
                <RefreshCw className="h-3.5 w-3.5" />
                Actualiser
              </button>
              <button
                onClick={handleAddExclusion}
                className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
              >
                <Plus className="h-3.5 w-3.5" />
                Ajouter
              </button>
            </div>
          }
        >
          {exclusions.length === 0 ? (
            <EmptyState
              icon={DollarSign}
              title="Aucune exclusion de charges"
              hint="Cet employé n'a pas d'exclusions de charges"
            />
          ) : (
            <div>
              <div className="mb-4 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                <input
                  type="text"
                  placeholder="Rechercher..."
                  value={exclusionSearchTerm}
                  onChange={(e) => setExclusionSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                />
              </div>
              <TableWrap>
                <thead>
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Type de charge</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Type d'exclusion</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Valeur fixe</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Motif</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Statut</th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-slate-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 bg-white">
                  {exclusions
                    .filter(e =>
                      getTypeChargeLabel(e.type_charge)?.toLowerCase().includes(exclusionSearchTerm.toLowerCase()) ||
                      getTypeExclusionLabel(e.type_exclusion)?.toLowerCase().includes(exclusionSearchTerm.toLowerCase()) ||
                      e.motif?.toLowerCase().includes(exclusionSearchTerm.toLowerCase())
                    )
                    .map((excl) => (
                    <tr key={excl.id} className="hover:bg-slate-50">
                      <td className="px-4 py-3 text-sm text-slate-900">{getTypeChargeLabel(excl.type_charge)}</td>
                      <td className="px-4 py-3 text-sm">
                        <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
                          excl.type_exclusion === 'zero' 
                            ? 'bg-red-100 text-red-600' 
                            : excl.type_exclusion === 'exempte'
                            ? 'bg-yellow-100 text-yellow-600'
                            : 'bg-blue-100 text-blue-600'
                        }`}>
                          {getTypeExclusionLabel(excl.type_exclusion)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-900">{excl.valeur_fixe || '-'}</td>
                      <td className="px-4 py-3 text-sm text-slate-500">{excl.motif || '-'}</td>
                      <td className="px-4 py-3 text-sm">
                        <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
                          excl.actif 
                            ? 'bg-green-100 text-green-600' 
                            : 'bg-slate-100 text-slate-600'
                        }`}>
                          {excl.actif ? 'Actif' : 'Inactif'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <div className="flex justify-center gap-2">
                          <button
                            onClick={() => handleEditExclusion(excl)}
                            className="text-blue-600 hover:text-blue-800"
                            title="Modifier"
                          >
                            <Edit className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteExclusion(excl.id)}
                            className="text-red-600 hover:text-red-800"
                            title="Supprimer"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </TableWrap>
            </div>
          )}
        </Section>
      )}

      {/* Modal d'ajout/modification d'avance */}
      {showAvanceModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="p-6 border-b border-slate-200">
              <h3 className="text-lg font-semibold text-slate-900">
                {editingAvance ? 'Modifier une avance sur salaire' : 'Ajouter une avance sur salaire'}
              </h3>
            </div>
            <form onSubmit={handleAvanceSubmit} className="p-6">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Montant net de l'avance</label>
                  <input
                    type="number"
                    value={avanceFormData.montant_avance}
                    onChange={(e) => setAvanceFormData({ ...avanceFormData, montant_avance: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Date de l'avance</label>
                  <input
                    type="date"
                    value={avanceFormData.date_avance}
                    onChange={(e) => setAvanceFormData({ ...avanceFormData, date_avance: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
              </div>
              <div className="flex justify-end gap-3 mt-6">
                <button
                  type="button"
                  onClick={() => setShowAvanceModal(false)}
                  className="px-4 py-2 text-sm font-medium text-slate-700 bg-slate-100 rounded-md hover:bg-slate-200"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
                >
                  {editingAvance ? 'Modifier' : 'Enregistrer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal d'ajout/modification de retenue */}
      {showRetenueModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="p-6 border-b border-slate-200">
              <h3 className="text-lg font-semibold text-slate-900">
                {editingRetenue ? 'Modifier une retenue sur salaire' : 'Ajouter une retenue sur salaire'}
              </h3>
            </div>
            <form onSubmit={handleRetenueSubmit} className="p-6">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Montant net de la retenue</label>
                  <input
                    type="number"
                    value={retenueFormData.montant_retenue}
                    onChange={(e) => setRetenueFormData({ ...retenueFormData, montant_retenue: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Date de la retenue</label>
                  <input
                    type="date"
                    value={retenueFormData.date_retenue}
                    onChange={(e) => setRetenueFormData({ ...retenueFormData, date_retenue: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Motif de la retenue</label>
                  <textarea
                    value={retenueFormData.motif_retenue}
                    onChange={(e) => setRetenueFormData({ ...retenueFormData, motif_retenue: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                    rows="3"
                  />
                </div>
              </div>
              <div className="flex justify-end gap-3 mt-6">
                <button
                  type="button"
                  onClick={() => setShowRetenueModal(false)}
                  className="px-4 py-2 text-sm font-medium text-slate-700 bg-slate-100 rounded-md hover:bg-slate-200"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
                >
                  {editingRetenue ? 'Modifier' : 'Enregistrer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal d'ajout/modification de régularisation */}
      {showRegularisationModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="p-6 border-b border-slate-200">
              <h3 className="text-lg font-semibold text-slate-900">
                {editingRegularisation ? 'Modifier une régularisation' : 'Ajouter une régularisation'}
              </h3>
            </div>
            <form onSubmit={handleRegularisationSubmit} className="p-6">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Salaire perçu mois dernier</label>
                  <input
                    type="number"
                    value={regularisationFormData.montant_salaire_percu}
                    onChange={(e) => setRegularisationFormData({ ...regularisationFormData, montant_salaire_percu: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Type de régularisation</label>
                  <select
                    value={regularisationFormData.type}
                    onChange={(e) => setRegularisationFormData({ ...regularisationFormData, type: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  >
                    <option value="0">Trop perçu</option>
                    <option value="1">Moins perçu</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Montant</label>
                  <input
                    type="number"
                    value={regularisationFormData.montant}
                    onChange={(e) => setRegularisationFormData({ ...regularisationFormData, montant: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Commentaire</label>
                  <textarea
                    value={regularisationFormData.commentaire}
                    onChange={(e) => setRegularisationFormData({ ...regularisationFormData, commentaire: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                    rows="3"
                  />
                </div>
              </div>
              <div className="flex justify-end gap-3 mt-6">
                <button
                  type="button"
                  onClick={() => setShowRegularisationModal(false)}
                  className="px-4 py-2 text-sm font-medium text-slate-700 bg-slate-100 rounded-md hover:bg-slate-200"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
                >
                  {editingRegularisation ? 'Modifier' : 'Enregistrer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal d'ajout à la caisse sociale */}
      {showCaisseModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="p-6 border-b border-slate-200">
              <h3 className="text-lg font-semibold text-slate-900">Inscrire à la caisse sociale</h3>
            </div>
            <form onSubmit={handleCaisseSubmit} className="p-6">
              <div className="space-y-4">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="approbation"
                    checked={caisseFormData.approbation}
                    onChange={(e) => setCaisseFormData({ ...caisseFormData, approbation: e.target.checked })}
                    className="rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                  />
                  <label htmlFor="approbation" className="ml-2 text-sm text-slate-700">Approuvé pour la caisse sociale</label>
                </div>
              </div>
              <div className="flex justify-end gap-3 mt-6">
                <button
                  type="button"
                  onClick={() => setShowCaisseModal(false)}
                  className="px-4 py-2 text-sm font-medium text-slate-700 bg-slate-100 rounded-md hover:bg-slate-200"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
                >
                  Inscrire
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal d'ajout/modification d'exclusion */}
      {showExclusionModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="p-6 border-b border-slate-200">
              <h3 className="text-lg font-semibold text-slate-900">
                {editingExclusion ? 'Modifier Exclusion' : 'Créer Exclusion'}
              </h3>
            </div>
            <form onSubmit={handleExclusionSubmit} className="p-6">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Type de charge</label>
                  <select
                    value={exclusionFormData.type_charge}
                    onChange={(e) => setExclusionFormData({ ...exclusionFormData, type_charge: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  >
                    <option value="cmu">CMU</option>
                    <option value="cnps">CNPS</option>
                    <option value="impot_salaire">Impôt salaire</option>
                    <option value="igr">IGR</option>
                    <option value="prime_anciennete">Prime ancienneté</option>
                    <option value="prime_variable">Prime variable</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Type d'exclusion</label>
                  <select
                    value={exclusionFormData.type_exclusion}
                    onChange={(e) => setExclusionFormData({ ...exclusionFormData, type_exclusion: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  >
                    <option value="exempte">Exempté (cotisation fixe)</option>
                    <option value="zero">Toujours 0</option>
                    <option value="fixe">Montant fixe personnalisé</option>
                  </select>
                </div>
                {exclusionFormData.type_exclusion === 'fixe' && (
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Valeur fixe</label>
                    <input
                      type="number"
                      value={exclusionFormData.valeur_fixe}
                      onChange={(e) => setExclusionFormData({ ...exclusionFormData, valeur_fixe: e.target.value })}
                      className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Montant fixe"
                    />
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Motif</label>
                  <input
                    type="text"
                    value={exclusionFormData.motif}
                    onChange={(e) => setExclusionFormData({ ...exclusionFormData, motif: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Motif de l'exclusion"
                  />
                </div>
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="actif"
                    checked={exclusionFormData.actif}
                    onChange={(e) => setExclusionFormData({ ...exclusionFormData, actif: e.target.checked ? 1 : 0 })}
                    className="rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                  />
                  <label htmlFor="actif" className="ml-2 text-sm text-slate-700">Actif</label>
                </div>
              </div>
              <div className="flex justify-end gap-3 mt-6">
                <button
                  type="button"
                  onClick={() => setShowExclusionModal(false)}
                  className="px-4 py-2 text-sm font-medium text-slate-700 bg-slate-100 rounded-md hover:bg-slate-200"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
                >
                  {editingExclusion ? 'Modifier' : 'Créer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal de suppression de régularisation */}
      {showDeleteRegularisationModal && selectedRegularisation && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
            <h3 className="text-lg font-semibold text-red-900 mb-2">Supprimer la régularisation</h3>
            <p className="text-sm text-slate-600 mb-2">
              Êtes-vous sûr de vouloir supprimer cette régularisation ?
            </p>
            <p className="text-sm text-slate-500 mb-4">
              Cette action est irréversible. Prière de générer à nouveau la paie de cet agent afin que ces nouveaux éléments soient pris en compte.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowDeleteRegularisationModal(false)}
                className="px-4 py-2 text-sm font-medium text-slate-700 bg-slate-100 rounded-md hover:bg-slate-200"
              >
                Annuler
              </button>
              <button
                onClick={handleDeleteRegularisationConfirm}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700"
              >
                Supprimer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de suppression de retenue */}
      {showDeleteRetenueModal && selectedRetenue && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
            <h3 className="text-lg font-semibold text-red-900 mb-2">Supprimer la retenue sur salaire</h3>
            <p className="text-sm text-slate-600 mb-2">
              Êtes-vous sûr de vouloir supprimer cette retenue sur salaire ?
            </p>
            <p className="text-sm text-slate-500 mb-4">
              Cette action est irréversible. Prière de générer à nouveau la paie de cet agent afin que ces nouveaux éléments soient pris en compte.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowDeleteRetenueModal(false)}
                className="px-4 py-2 text-sm font-medium text-slate-700 bg-slate-100 rounded-md hover:bg-slate-200"
              >
                Annuler
              </button>
              <button
                onClick={handleDeleteRetenueConfirm}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700"
              >
                Supprimer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de suppression d'avance */}
      {showDeleteAvanceModal && selectedAvance && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
            <h3 className="text-lg font-semibold text-red-900 mb-2">Supprimer l'avance sur salaire</h3>
            <p className="text-sm text-slate-600 mb-2">
              Êtes-vous sûr de vouloir supprimer cette avance sur salaire ?
            </p>
            <p className="text-sm text-slate-500 mb-4">
              Cette action est irréversible. Prière de générer à nouveau la paie de cet agent afin que ces nouveaux éléments soient pris en compte.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowDeleteAvanceModal(false)}
                className="px-4 py-2 text-sm font-medium text-slate-700 bg-slate-100 rounded-md hover:bg-slate-200"
              >
                Annuler
              </button>
              <button
                onClick={handleDeleteAvanceConfirm}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700"
              >
                Supprimer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de modification des infos paie */}
      {showEditModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-slate-200">
              <h3 className="text-lg font-semibold text-slate-900">Modifier les informations de paie</h3>
            </div>
            <form onSubmit={handleSaveInfopaie} className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Type de paiement</label>
                  <select
                    value={formData.type_paiement}
                    onChange={(e) => setFormData({ ...formData, type_paiement: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Sélectionner</option>
                    {TYPE_PAIEMENT_OPTIONS.map((opt) => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Banque</label>
                  <select
                    value={formData.banque}
                    onChange={(e) => setFormData({ ...formData, banque: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Sélectionner</option>
                    {banques.map((b) => (
                      <option key={b.idbanque} value={b.idbanque}>{b.libelle_banque}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Numéro de compte</label>
                  <input
                    type="text"
                    value={formData.numero_compte}
                    onChange={(e) => setFormData({ ...formData, numero_compte: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Prime de déplacement</label>
                  <input
                    type="text"
                    value={formData.prime_deplacement}
                    onChange={(e) => setFormData({ ...formData, prime_deplacement: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Prime d'astreinte</label>
                  <input
                    type="text"
                    value={formData.prime_de_astreinte}
                    onChange={(e) => setFormData({ ...formData, prime_de_astreinte: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Prime de perdiem</label>
                  <input
                    type="text"
                    value={formData.prime_de_perdiem}
                    onChange={(e) => setFormData({ ...formData, prime_de_perdiem: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Prime de caisse</label>
                  <input
                    type="text"
                    value={formData.prime_de_caisse}
                    onChange={(e) => setFormData({ ...formData, prime_de_caisse: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Prime de responsabilité</label>
                  <input
                    type="text"
                    value={formData.prime_de_responsabilite}
                    onChange={(e) => setFormData({ ...formData, prime_de_responsabilite: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Prime d'éloignement</label>
                  <input
                    type="text"
                    value={formData.prime_de_eloignement}
                    onChange={(e) => setFormData({ ...formData, prime_de_eloignement: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
              <div className="flex justify-end gap-3 mt-6">
                <button
                  type="button"
                  onClick={() => setShowEditModal(false)}
                  className="px-4 py-2 text-sm font-medium text-slate-700 bg-slate-100 rounded-md hover:bg-slate-200"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
                >
                  Enregistrer
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </Panel>
  );
};

const Documents = ({ employe }) => {
  const [loading, setLoading] = useState(true);
  const [documents, setDocuments] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editData, setEditData] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [formData, setFormData] = useState({
    personnel_id: employe?.idpersonnel || '',
    type_document: '',
    titre: '',
    date_obtention: '',
    date_expiration: '',
    fichier: null
  });

  const DOCUMENT_TYPES = [
    { id: 'diplome', label: 'Diplôme', icon: '🎓' },
    { id: 'certificat', label: 'Certificat', icon: '📜' },
    { id: 'piece_identite', label: 'Pièce d\'identité', icon: '🪪' },
    { id: 'cv', label: 'CV', icon: '📄' },
    { id: 'lettre_recommandation', label: 'Lettre de recommandation', icon: '✉️' },
    { id: 'attestation', label: 'Attestation', icon: '📋' },
    { id: 'permis', label: 'Permis', icon: '🚗' },
    { id: 'autre', label: 'Autre', icon: '📁' }
  ];

  useEffect(() => {
    loadDocuments();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employe?.idpersonnel]);

  const loadDocuments = async () => {
    if (!employe?.idpersonnel) return;
    try {
      setLoading(true);
      const response = await http.get(`/api/documents-admin/personnel/${employe.idpersonnel}`);
      setDocuments(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Erreur chargement documents:', error);
      setDocuments([]);
    } finally {
      setLoading(false);
    }
  };

  const getDocumentTypeInfo = (typeId) => {
    return DOCUMENT_TYPES.find(t => t.id === typeId) || DOCUMENT_TYPES[DOCUMENT_TYPES.length - 1];
  };

  const getStatutBadge = (dateExpiration) => {
    if (!dateExpiration) {
      return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">Valide</span>;
    }
    const today = new Date();
    const expDate = new Date(dateExpiration);
    const daysUntilExpiry = Math.ceil((expDate - today) / (1000 * 60 * 60 * 24));

    if (daysUntilExpiry < 0) {
      return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">Expiré</span>;
    } else if (daysUntilExpiry <= 30) {
      return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">À renouveler</span>;
    } else {
      return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">Valide</span>;
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData({ ...formData, fichier: file });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const formDataToSend = new FormData();
      formDataToSend.append('personnel_id', formData.personnel_id);
      formDataToSend.append('type_document', formData.type_document);
      formDataToSend.append('titre', formData.titre);
      formDataToSend.append('date_obtention', formData.date_obtention);
      if (formData.date_expiration) {
        formDataToSend.append('date_expiration', formData.date_expiration);
      }
      formDataToSend.append('filiale_id', employe.filiale_id);
      if (formData.fichier instanceof File) {
        formDataToSend.append('fichier', formData.fichier);
      }

      if (editData) {
        // Modification
        await http.put(`/api/documents-admin/${editData.id}`, formDataToSend, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      } else {
        // Création
        await http.post('/api/documents-admin', formDataToSend, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      }

      setShowModal(false);
      setEditData(null);
      setFormData({
        personnel_id: employe.idpersonnel,
        type_document: '',
        titre: '',
        date_obtention: '',
        date_expiration: '',
        fichier: null
      });
      loadDocuments();
    } catch (error) {
      console.error('Erreur soumission:', error);
      alert('Erreur lors de la soumission');
    }
  };

  const handleEdit = (doc) => {
    setEditData(doc);
    setFormData({
      personnel_id: doc.personnel_id,
      type_document: doc.type_document,
      titre: doc.titre,
      date_obtention: doc.date_obtention,
      date_expiration: doc.date_expiration,
      fichier: doc.fichier
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce document ?')) return;
    try {
      await http.delete(`/api/documents-admin/${id}`);
      loadDocuments();
    } catch (error) {
      console.error('Erreur suppression:', error);
      alert('Erreur lors de la suppression');
    }
  };

  const handleDownload = async (doc) => {
    try {
      const response = await http.get(`/api/documents-admin/${doc.id}/download`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${doc.titre}${doc.fichier ? doc.fichier.substring(doc.fichier.lastIndexOf('.')) : '.pdf'}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Erreur téléchargement:', error);
      alert('Erreur lors du téléchargement');
    }
  };

  const filteredDocuments = documents.filter(doc =>
    doc.titre?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    getDocumentTypeInfo(doc.type_document).label.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return <LoadingBlock />;
  }

  return (
    <Panel title="Documents administratifs" action={
      <button
        onClick={() => {
          setEditData(null);
          setFormData({
            personnel_id: employe.idpersonnel,
            type_document: '',
            titre: '',
            date_obtention: '',
            date_expiration: '',
            fichier: null
          });
          setShowModal(true);
        }}
        className="inline-flex items-center gap-1 text-xs font-medium text-blue-700 hover:text-blue-800"
      >
        <Plus className="h-3.5 w-3.5" />
        Ajouter un document
      </button>
    }>
      <Section title="Bibliothèque administrative" plain>
        <div className="mb-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
            <input
              type="text"
              placeholder="Rechercher par titre ou type..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-sky-500 focus:outline-none"
            />
          </div>
        </div>
        {filteredDocuments.length === 0 ? (
          <EmptyState
            icon={Folder}
            title={documents.length === 0 ? "Aucun document administratif" : "Aucun résultat"}
            hint={documents.length === 0 ? "Ajoutez les diplômes, certificats et autres documents de l'employé" : "Aucun document ne correspond à votre recherche"}
          />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredDocuments.map((doc) => {
              const typeInfo = getDocumentTypeInfo(doc.type_document);
              return (
                <div key={doc.id} className="group relative overflow-hidden rounded-xl border border-slate-200 bg-white p-5 hover:border-sky-300 hover:shadow-md transition-all">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <span className="text-3xl">{typeInfo.icon}</span>
                      <div>
                        <h4 className="font-semibold text-slate-900">{doc.titre}</h4>
                        <p className="text-xs text-slate-500">{typeInfo.label}</p>
                      </div>
                    </div>
                    {getStatutBadge(doc.date_expiration)}
                  </div>

                  <div className="space-y-2 text-sm text-slate-600 mb-4">
                    <div className="flex items-center gap-2">
                      <Calendar className="h-4 w-4 text-slate-400" />
                      <span>Obtention : {doc.date_obtention ? new Date(doc.date_obtention).toLocaleDateString('fr-FR') : '-'}</span>
                    </div>
                    {doc.date_expiration && (
                      <div className="flex items-center gap-2">
                        <AlertTriangle className="h-4 w-4 text-slate-400" />
                        <span>Expiration : {new Date(doc.date_expiration).toLocaleDateString('fr-FR')}</span>
                      </div>
                    )}
                    <div className="flex items-center gap-2">
                      <FileText className="h-4 w-4 text-slate-400" />
                      <span className="truncate">{typeof doc.fichier === 'string' ? doc.fichier : doc.fichier?.name || 'Fichier'}</span>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 pt-3 border-t border-slate-100">
                    <button
                      onClick={() => handleDownload(doc)}
                      className="flex-1 flex items-center justify-center gap-1 px-3 py-2 text-sm font-medium text-sky-700 bg-sky-50 rounded-lg hover:bg-sky-100 transition-colors"
                    >
                      <Download className="h-4 w-4" />
                      Télécharger
                    </button>
                    <button
                      onClick={() => handleEdit(doc)}
                      className="p-2 text-slate-500 hover:text-sky-600 hover:bg-slate-50 rounded-lg transition-colors"
                      title="Modifier"
                    >
                      <Edit className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(doc.id)}
                      className="p-2 text-slate-500 hover:text-red-600 hover:bg-slate-50 rounded-lg transition-colors"
                      title="Supprimer"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Section>

      {/* Modal création/modification */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="w-full max-w-2xl rounded-2xl bg-white p-6 shadow-2xl">
            <div className="mb-4 flex items-center justify-between border-b border-slate-200 pb-4">
              <h3 className="text-lg font-bold text-slate-900">
                {editData ? 'Modifier un document' : 'Ajouter un document'}
              </h3>
              <button
                onClick={() => {
                  setShowModal(false);
                  setEditData(null);
                  setFormData({
                    personnel_id: employe.idpersonnel,
                    type_document: '',
                    titre: '',
                    date_obtention: '',
                    date_expiration: '',
                    fichier: null
                  });
                }}
                className="text-slate-400 hover:text-slate-600"
              >
                ✕
              </button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="space-y-4">
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-slate-700">
                    Type de document
                  </label>
                  <select
                    className="w-full rounded-lg border border-slate-200 px-4 py-2 text-sm focus:border-sky-500 focus:outline-none"
                    value={formData.type_document}
                    onChange={(e) => setFormData({ ...formData, type_document: e.target.value })}
                    required
                  >
                    <option value="">Sélectionner</option>
                    {DOCUMENT_TYPES.map(type => (
                      <option key={type.id} value={type.id}>{type.icon} {type.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-slate-700">
                    Titre du document
                  </label>
                  <input
                    type="text"
                    className="w-full rounded-lg border border-slate-200 px-4 py-2 text-sm focus:border-sky-500 focus:outline-none"
                    value={formData.titre}
                    onChange={(e) => setFormData({ ...formData, titre: e.target.value })}
                    placeholder="Ex: Licence en Informatique"
                    required
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="mb-1.5 block text-sm font-medium text-slate-700">
                      Date d'obtention
                    </label>
                    <input
                      type="date"
                      className="w-full rounded-lg border border-slate-200 px-4 py-2 text-sm focus:border-sky-500 focus:outline-none"
                      value={formData.date_obtention}
                      onChange={(e) => setFormData({ ...formData, date_obtention: e.target.value })}
                      required
                    />
                  </div>
                  <div>
                    <label className="mb-1.5 block text-sm font-medium text-slate-700">
                      Date d'expiration (optionnel)
                    </label>
                    <input
                      type="date"
                      className="w-full rounded-lg border border-slate-200 px-4 py-2 text-sm focus:border-sky-500 focus:outline-none"
                      value={formData.date_expiration}
                      onChange={(e) => setFormData({ ...formData, date_expiration: e.target.value })}
                    />
                  </div>
                </div>
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-slate-700">
                    Fichier
                  </label>
                  <input
                    type="file"
                    className="w-full rounded-lg border border-slate-200 px-4 py-2 text-sm focus:border-sky-500 focus:outline-none"
                    onChange={handleFileChange}
                    accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                    required={!editData}
                  />
                  <p className="mt-1 text-xs text-slate-500">Formats acceptés : PDF, DOC, DOCX, JPG, PNG</p>
                </div>
              </div>
              <div className="mt-6 flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    setEditData(null);
                    setFormData({
                      personnel_id: employe.idpersonnel,
                      type_document: '',
                      titre: '',
                      date_obtention: '',
                      date_expiration: '',
                      fichier: null
                    });
                  }}
                  className="px-4 py-2 rounded-lg border border-slate-200 text-slate-700 hover:bg-slate-50"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 rounded-lg bg-sky-600 text-white hover:bg-sky-700"
                >
                  {editData ? 'Modifier' : 'Ajouter'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </Panel>
  );
};

const CompteUtilisateur = ({ employe }) => {
  const [loading, setLoading] = useState(true);
  const [userAccount, setUserAccount] = useState(null);
  const [showResetModal, setShowResetModal] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [resetError, setResetError] = useState('');

  useEffect(() => {
    loadUserAccount();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employe?.idpersonnel]);

  const loadUserAccount = async () => {
    if (!employe?.idpersonnel) return;
    try {
      setLoading(true);
      const response = await http.get(`/api/users/personnel/${employe.idpersonnel}`);
      setUserAccount(response.data);
    } catch (error) {
      if (error.response?.status === 404) {
        setUserAccount(null);
      } else {
        console.error('Erreur chargement compte utilisateur:', error);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (newPassword.length < 6) {
      setResetError('Le mot de passe doit contenir au moins 6 caractères');
      return;
    }

    try {
      await http.post(`/api/users/${userAccount.iduser}/reset-password`, { newPassword });
      setShowResetModal(false);
      setNewPassword('');
      setResetError('');
      alert('Mot de passe réinitialisé avec succès');
    } catch (error) {
      console.error('Erreur réinitialisation mot de passe:', error);
      setResetError('Erreur lors de la réinitialisation du mot de passe');
    }
  };

  const handleToggleLock = async () => {
    try {
      const newEtat = userAccount.etat === 0 ? 1 : 0; // 0=actif, 1=inactif
      await http.put(`/api/users/${userAccount.iduser}/lock`, { etat: newEtat });
      setUserAccount({ ...userAccount, etat: newEtat });
      alert(newEtat === 0 ? 'Compte déverrouillé avec succès' : 'Compte verrouillé avec succès');
    } catch (error) {
      console.error('Erreur verrouillage compte:', error);
      alert('Erreur lors de la modification du statut du compte');
    }
  };

  if (loading) {
    return <LoadingBlock />;
  }

  return (
    <Panel title="Compte utilisateur">
      {!userAccount ? (
        <Section title="Aucun compte utilisateur" plain>
          <div className="text-center py-8">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-slate-100 mx-auto mb-4">
              <User className="h-6 w-6 text-slate-400" />
            </div>
            <p className="text-slate-600 mb-4">Cet employé n'a pas encore de compte utilisateur pour accéder à l'espace employé.</p>
            <button
              onClick={() => window.location.href = '/users'}
              className={btnPrimary}
            >
              Créer un compte utilisateur
            </button>
          </div>
        </Section>
      ) : (
        <>
          <Section title="Informations du compte" plain>
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 rounded-lg bg-slate-50">
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-sky-100">
                    <User className="h-5 w-5 text-sky-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-slate-900">{userAccount.username}</p>
                    <p className="text-xs text-slate-500">Nom d'utilisateur</p>
                  </div>
                </div>
                <Badge tone={userAccount.etat === 0 ? 'green' : 'red'}>
                  {userAccount.etat === 0 ? 'Actif' : 'Verrouillé'}
                </Badge>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 rounded-lg bg-slate-50">
                  <p className="text-xs text-slate-500 mb-1">Nom complet</p>
                  <p className="text-sm font-medium text-slate-900">{userAccount.nom} {userAccount.prenom}</p>
                </div>
                <div className="p-4 rounded-lg bg-slate-50">
                  <p className="text-xs text-slate-500 mb-1">Profil</p>
                  <p className="text-sm font-medium text-slate-900">{userAccount.profil_nom || userAccount.profil || '-'}</p>
                </div>
              </div>

              <div className="p-4 rounded-lg bg-slate-50">
                <p className="text-xs text-slate-500 mb-1">Email associé</p>
                <p className="text-sm font-medium text-slate-900">{employe.adressemail || '-'}</p>
              </div>
            </div>
          </Section>

          <Section title="Actions de sécurité" plain>
            <div className="space-y-3">
              <button
                onClick={() => setShowResetModal(true)}
                className="w-full flex items-center justify-between gap-3 p-4 rounded-lg border border-slate-200 hover:border-sky-300 hover:bg-sky-50 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-amber-100">
                    <Lock className="h-4 w-4 text-amber-600" />
                  </div>
                  <div className="text-left">
                    <p className="text-sm font-medium text-slate-900">Réinitialiser le mot de passe</p>
                    <p className="text-xs text-slate-500">Définir un nouveau mot de passe pour l'employé</p>
                  </div>
                </div>
                <ChevronRight className="h-5 w-5 text-slate-400" />
              </button>

              <button
                onClick={handleToggleLock}
                className="w-full flex items-center justify-between gap-3 p-4 rounded-lg border border-slate-200 hover:border-sky-300 hover:bg-sky-50 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className={`flex h-9 w-9 items-center justify-center rounded-lg ${userAccount.etat === 0 ? 'bg-red-100' : 'bg-green-100'}`}>
                    {userAccount.etat === 0 ? (
                      <Lock className="h-4 w-4 text-red-600" />
                    ) : (
                      <Unlock className="h-4 w-4 text-green-600" />
                    )}
                  </div>
                  <div className="text-left">
                    <p className="text-sm font-medium text-slate-900">
                      {userAccount.etat === 0 ? 'Verrouiller le compte' : 'Déverrouiller le compte'}
                    </p>
                    <p className="text-xs text-slate-500">
                      {userAccount.etat === 0 ? 'Empêcher l\'accès à l\'espace employé' : 'Autoriser l\'accès à l\'espace employé'}
                    </p>
                  </div>
                </div>
                <ChevronRight className="h-5 w-5 text-slate-400" />
              </button>
            </div>
          </Section>
        </>
      )}

      {/* Modal de réinitialisation du mot de passe */}
      {showResetModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl">
            <div className="mb-4 flex items-center justify-between border-b border-slate-200 pb-4">
              <h3 className="text-lg font-bold text-slate-900">Réinitialiser le mot de passe</h3>
              <button
                onClick={() => {
                  setShowResetModal(false);
                  setNewPassword('');
                  setResetError('');
                }}
                className="text-slate-400 hover:text-slate-600"
              >
                ✕
              </button>
            </div>
            <form onSubmit={handleResetPassword}>
              <div className="space-y-4">
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-slate-700">
                    Nouveau mot de passe
                  </label>
                  <input
                    type="password"
                    className="w-full rounded-lg border border-slate-200 px-4 py-2 text-sm focus:border-sky-500 focus:outline-none"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Minimum 6 caractères"
                    required
                  />
                  {resetError && (
                    <p className="mt-1 text-xs text-red-600">{resetError}</p>
                  )}
                </div>
              </div>
              <div className="mt-6 flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowResetModal(false);
                    setNewPassword('');
                    setResetError('');
                  }}
                  className="px-4 py-2 rounded-lg border border-slate-200 text-slate-700 hover:bg-slate-50"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 rounded-lg bg-sky-600 text-white hover:bg-sky-700"
                >
                  Réinitialiser
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </Panel>
  );
};

export default EmployeDetail;